/**
 * Generic document viewer — a branded, print-ready A4 page and an 80mm thermal
 * receipt, rendered from real document/party/tenant data. Shared by the sales
 * invoice (InvoicePrintPage) and the purchase invoice / bill (BillPrintPage)
 * via a small config; the only differences are the API endpoint, the back
 * path, the document title, the party-block label (Bill to / Supplier), and
 * the status namespace for the watermark.
 *
 * TWO data sources, ONE layout. Given a `config` it makes four authed reads;
 * given a `publicToken` it makes one anonymous read of `/public/documents/:token`
 * and fills the same four state slots. That seam is the whole reason the
 * customer's no-login copy is the same paper the staff print rather than a
 * second implementation that quietly drifts — everything below the fetch
 * (i18n, brand, palette, amountInWords, code128) already works without a
 * session.
 *
 * No app shell: a slim viewer chrome (back · language · format toggle · print)
 * over a white "paper". The CLASSIC A4 sheet is deliberately bare at the foot —
 * no signature rule, no document barcode, no share QR — because the shop's own
 * A4 invoice carries none of them and this is meant to match it; only the
 * spelled-out total was added later, by request. The A42 face is the opposite
 * bargain: same tree, professional coat, and it DOES carry the share QR and
 * signature lines. The 80mm receipt
 * still prints both codes: that one is scanned at a counter to pull a sale up
 * for a return or a reprint, and its QR (see `publicLinkEndpoint`) opens the
 * customer's no-login copy; supplier documents get none. The paper's brand
 * colour comes from the active palette's
 * BASE primary (saturated, reads on white, and tracks the palette) rather than
 * the theme-lifted brand token, so it looks right whether the app is in light
 * or dark mode. Ctrl/⌘+P (or the Print button) renders it to PDF via the
 * browser — no server-side PDF, so the deploy footprint stays tiny.
 */

import { code128Svg, type WhatsAppDocType } from '@erp/shared';
import { ArrowLeft, Printer, SlidersHorizontal, Smartphone } from 'lucide-react';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { usePermission } from '../../hooks/usePermission.js';
import { amountInWords } from '../../lib/amountInWords.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { getBrand } from '../../lib/brandClient.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney, getCurrencyDecimals, setCurrencyDecimals } from '../../lib/money.js';
import { assetUrl } from '../../lib/publicApi.js';
import { getPublicDocument, type DisplayFxRate, type PublicPaperDocument } from '../../lib/publicDocument.js';
import { unitLabel } from '../../lib/unitLabel.js';
import { WhatsAppButton } from '../common/WhatsAppButton.js';

import { cashNotes, PAID_LABEL_DEFAULT, paidLabelKey } from './cashNotes.js';
import { splitNote } from './docNotes.js';
import { DOC_PRINT_CONFIGS } from './docPrintConfigs.js';
import { descriptionAddsNothing } from './lineDescription.js';

/** Wrap a phone number so its leading "+" stays on the left even inside an RTL
 *  (Arabic) document — otherwise bidi reordering pushes the "+" to the end. */
/**
 * An address as one line, street first. Object.values() read it in whatever
 * order the JSON came back — MySQL returns JSON keys sorted by length, so
 * "Erbil, 12 Market St" put the city before the street. Known parts in postal
 * order, anything else after; the comma follows the ADDRESS's own script (an
 * English address on an Arabic paper keeps its English comma). Render it in a
 * <bdi> so it keeps its own direction inside the other language's line.
 */
const ADDRESS_ORDER = ['line1', 'line2', 'street', 'district', 'city', 'region', 'postalCode', 'country'];
function addressText(addr: Record<string, unknown> | null | undefined): string {
  if (!addr) return '';
  const keys = [...ADDRESS_ORDER.filter((k) => k in addr), ...Object.keys(addr).filter((k) => !ADDRESS_ORDER.includes(k))];
  const parts = keys
    .map((k) => addr[k])
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());
  return parts.join(parts.some((v) => /[؀-ۿ]/.test(v)) ? '، ' : ', ');
}

function Tel({ children }: { children: string }): JSX.Element {
  return <bdi dir="ltr" style={{ unicodeBidi: 'isolate' }}>{children}</bdi>;
}

/**
 * Per-user preference: print a scannable barcode beside every line.
 *
 * On by default — that is the point of the feature — but a switch, because bars
 * per line are what turn a one-page invoice into three, and on an 80mm roll they
 * are real paper. Stored per user like the colour override, not per document.
 */
/**
 * Every sheet preference on this page is an `erp-doc-*` localStorage entry, so
 * "save these settings for the shop" is exactly: copy those entries into one
 * tenant setting, and seed a browser that has none of its own from it.
 *
 * Per-browser still WINS — a cashier who narrowed a column keeps it — because
 * the shop default is a starting point, not a policy. That is also why the seed
 * only fills keys the browser has never set.
 */
const DOC_PREFS_SETTING = 'doc_print';
const PREF_PREFIX = 'erp-doc-';

/** The browser's current sheet preferences, as one flat object. */
function collectPrefs(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (!k || !k.startsWith(PREF_PREFIX)) continue;
      const v = window.localStorage.getItem(k);
      if (v !== null) out[k] = v;
    }
  } catch { /* private mode — nothing to save */ }
  return out;
}

/** Fill in only what this browser has never chosen for itself. */
function seedPrefs(saved: Record<string, unknown>): void {
  try {
    for (const [k, v] of Object.entries(saved)) {
      if (!k.startsWith(PREF_PREFIX) || typeof v !== 'string') continue;
      if (window.localStorage.getItem(k) === null) window.localStorage.setItem(k, v);
    }
  } catch { /* ignore */ }
}

const LINE_BARCODES_KEY = 'erp-doc-line-barcodes';
function loadLineBarcodes(): boolean {
  try { return window.localStorage.getItem(LINE_BARCODES_KEY) !== '0'; } catch { return true; }
}
function saveLineBarcodes(on: boolean): void {
  try { window.localStorage.setItem(LINE_BARCODES_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

/**
 * Whether a CONVERTED sheet prints the line explaining the conversion.
 *
 * Defaults ON, and that default is the safe one: without the note a sheet
 * reading "USD 19.32" cannot be told apart from a sale actually made in
 * dollars. It is a switch because a shop that converts for a regular customer
 * every week does not need the sentence every time — but turning it off is a
 * deliberate act, not the default.
 */
const FX_NOTE_KEY = 'erp-doc-fx-note';
function loadFxNote(): boolean {
  try { return window.localStorage.getItem(FX_NOTE_KEY) !== '0'; } catch { return true; }
}
function saveFxNote(on: boolean): void {
  try { window.localStorage.setItem(FX_NOTE_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

/**
 * The language of the PAPER — deliberately not the language of the app.
 *
 * The person printing and the person being handed the sheet are two different
 * people: a shop that runs its admin in Arabic still sells to customers who
 * read English, and re-skinning the whole dashboard to print one invoice is not
 * a workflow. So this drives the document only, via `getFixedT` — both catalogs
 * are compiled into the bundle (lib/i18n.ts imports them statically), so there
 * is nothing to load and no async seam.
 *
 * 'both' prints every LABEL in both languages, one under the other. Only labels:
 * item names, codes and units are the shop's own data in whatever language they
 * were typed, and there is no second copy of them to print.
 *
 * '' = follow the app, which is what everyone got before this existed.
 */
type DocLang = '' | 'ar' | 'en' | 'both';
const DOC_LANG_KEY = 'erp-doc-lang';
function loadDocLang(): DocLang {
  try {
    const v = window.localStorage.getItem(DOC_LANG_KEY);
    return v === 'ar' || v === 'en' || v === 'both' ? v : '';
  } catch { return ''; }
}
function saveDocLang(v: DocLang): void {
  try { window.localStorage.setItem(DOC_LANG_KEY, v); } catch { /* ignore */ }
}

/**
 * One paper label: the chosen language, with the other underneath in bilingual
 * mode. Identical strings print once — an untranslated key, or a label like "#"
 * that is the same in both, would otherwise be stamped out twice.
 */
function Bi({ a, b }: { a: string; b: string | null }): JSX.Element {
  return <>{a}{b && b !== a ? <span className="alt">{b}</span> : null}</>;
}

/**
 * The blank top/bottom/side the "data only" (A5 plain) mode leaves for the
 * shop's OWN pre-printed paper.
 *
 * Per user (per machine) like the colour override — not per document, not per
 * tenant: it is a property of the paper stack sitting in that one printer, and
 * the person who loaded it is the only one who can measure it. A hard-coded
 * guess prints the invoice straight over the letterhead.
 */
const PLAIN_KEYS = { top: 'erp-doc-plain-top', bottom: 'erp-doc-plain-bottom', side: 'erp-doc-plain-side' } as const;
// 40mm of letterhead at the top, per the shop. It is only a starting point; the
// operator measures their own stack and the value sticks per browser — so a
// browser that has already saved a number keeps it, and this default only
// applies where nobody has set one.
const PLAIN_DEFAULTS = { top: 40, bottom: 30, side: 10 };   // mm
// The ceiling has to clear the default, and 150mm still leaves a printable band on
// a 210mm-tall A5. Anything approaching the full height is a mis-typed value, not a
// letterhead.
const PLAIN_MAX = { top: 150, bottom: 90, side: 40 };
function loadMm(k: keyof typeof PLAIN_KEYS): number {
  try {
    const raw = window.localStorage.getItem(PLAIN_KEYS[k]);
    // `Number(null)` is 0, NOT NaN — so a missing key sailed through the finite
    // check below and reserved NO space at all. That is the one failure this whole
    // option exists to prevent: on every browser that had never set a value (i.e.
    // everyone's first print) the A5 sheet came out with 0mm margins, straight over
    // the letterhead. An empty string coerces to 0 the same way.
    if (raw === null || raw.trim() === '') return PLAIN_DEFAULTS[k];
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= PLAIN_MAX[k] ? v : PLAIN_DEFAULTS[k];
  } catch { return PLAIN_DEFAULTS[k]; }
}
function saveMm(k: keyof typeof PLAIN_KEYS, v: number): void {
  try { window.localStorage.setItem(PLAIN_KEYS[k], String(v)); } catch { /* ignore */ }
}

/**
 * Which columns of the line table the sheet inks.
 *
 * One paper serves very different counters: a wholesale desk wants the code and
 * the pack size, a delivery note wants neither and frequently no prices at all,
 * and a shop with no photographs in its catalogue just wants that column gone.
 * So the columns belong to the operator, per browser like the margins above —
 * the document itself keeps every figure either way, this only decides what is
 * printed.
 *
 * Stored as the list of HIDDEN keys rather than the whole map on purpose: a
 * column added to the sheet later is simply absent from everyone's stored list
 * and prints by default, where a saved map would have silently dropped it on
 * every browser that had ever opened this page.
 */
const COL_KEYS = ['img', 'code', 'pack', 'unit', 'qty', 'price', 'tax', 'total'] as const;
type ColKey = (typeof COL_KEYS)[number];
type DocCols = Record<ColKey, boolean>;
/**
 * Hidden columns are remembered PER DOCUMENT TYPE.
 *
 * One shared key would carry a choice across documents that are not alike: a
 * clerk who hides price and total to print a sales order as a picking list
 * would, ten minutes later, hand a customer an invoice with no prices on it —
 * silently, because the panel is collapsed by default and the sheet looks
 * deliberate. A delivery note and an invoice are different documents and get
 * different memories.
 */
const COLS_KEY_BASE = 'erp-doc-cols';
const colsKeyFor = (docType: string): string => `${COLS_KEY_BASE}:${docType}`;
const ALL_COLS: DocCols = { img: true, code: true, pack: true, unit: true, qty: true, price: true, tax: true, total: true };
function loadCols(docType: string): DocCols {
  try {
    const raw = window.localStorage.getItem(colsKeyFor(docType));
    if (!raw) return { ...ALL_COLS };
    const hidden = raw.split(',');
    const cols = { ...ALL_COLS };
    for (const k of COL_KEYS) if (hidden.includes(k)) cols[k] = false;
    return cols;
  } catch { return { ...ALL_COLS }; }
}
function saveCols(docType: string, c: DocCols): void {
  try {
    window.localStorage.setItem(colsKeyFor(docType), COL_KEYS.filter((k) => !c[k]).join(','));
  } catch { /* ignore */ }
}

/**
 * Print everything one weight heavier.
 *
 * For the printer whose toner is on its way out and the shop that is not
 * replacing it this month: the sheet's body ink is a light grey by design (it
 * is the old system's palette, kept), which is exactly the text such a printer
 * loses first. Off by default — the default sheet must stay the sheet the shop
 * already knows.
 */
const BOLD_KEY = 'erp-doc-bold';
function loadBold(): boolean {
  try { return window.localStorage.getItem(BOLD_KEY) === '1'; } catch { return false; }
}
function saveBold(on: boolean): void {
  try { window.localStorage.setItem(BOLD_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

/**
 * Print the document's own notes.
 *
 * The till writes its own working note onto every sale it rings up — "POS
 * terminal · cash" — and that is bookkeeping, not something a customer should
 * read on their invoice. Rather than filter particular strings (today's prefix
 * is not tomorrow's), the whole field is made switchable: on a typed invoice
 * the note is a delivery instruction and belongs on the paper, on a till sale
 * it is noise. On by default, so the sheet stays the sheet the shop knows.
 */
const NOTES_KEY = 'erp-doc-notes';
function loadShowNotes(): boolean {
  try { return window.localStorage.getItem(NOTES_KEY) !== '0'; } catch { return true; }
}
function saveShowNotes(on: boolean): void {
  try { window.localStorage.setItem(NOTES_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

/**
 * The tinted table head on the A5 sheet.
 *
 * A4 has always carried the old system's #E1ECFF head. A5 stripped it, on the
 * reasoning that the mode prints onto the shop's own pre-printed paper and a
 * wash is ink rather than data. That holds for the letterhead, the footer and
 * the QR — those really are already on the page — but a table heading is not
 * pre-printed on anything, so the sheet just came out looking unlike the A4 of
 * the same document. Tinted by default now; the switch is there for whoever is
 * still feeding the printer pre-printed stock and counting toner.
 */
/**
 * The rest of the sheet preferences, which are all one stored string.
 *
 * Written as a pair of tiny accessors rather than another four load/save
 * couples: they differ only in their key and their default, and four more
 * near-identical functions would bury the two above that actually carry an
 * argument worth reading.
 */
/** Which endpoints accept a note-only save, and the right it takes. */
const NOTE_SAVE_PERMISSION: Record<string, string> = {
  '/invoices': 'invoices.write',
  '/sales-orders': 'sales_orders.write',
  '/quotations': 'quotations.write',
  '/bills': 'bills.write',
  '/purchase-orders': 'purchase_orders.write',
};

function loadPref(key: string, fallback: string): string {
  try { return window.localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
function savePref(key: string, v: string): void {
  try { window.localStorage.setItem(key, v); } catch { /* ignore */ }
}

/** A5 only: bring back the letterhead band and the foot the plain mode drops. */
const A5_HEAD_KEY = 'erp-doc-a5head';
const A5_FOOT_KEY = 'erp-doc-a5foot';
/**
 * The wash behind the table heading. Default is the old system's own blue.
 *
 * Held as custom-property NAMES, not hex: colours belong in the stylesheet in
 * this codebase (there is a lint rule that says so), and it means the swatch
 * and the sheet read the same value from one place instead of two.
 */
const TINT_KEY = 'erp-doc-tint';
const DEFAULT_TINT = '--tint-1';
/** Named so the swatch row stays a list rather than five copies of a button. */
const TINTS = ['--tint-1', '--tint-2', '--tint-3', '--tint-4', '--tint-5'] as const;
/** Tighter line rows. The A5 body is ~140mm; this is what decides how many fit. */
const DENSE_KEY = 'erp-doc-dense';

/**
 * The sheet's face and its size.
 *
 * Every size on this paper is an absolute pixel copied from the shop's previous
 * system, so a size control means multiplying each of them through one variable
 * rather than setting a base and hoping it cascades — it would not, none of
 * them are relative. The default is 1 and the default face is the original, so
 * a shop that never opens this panel keeps the replica exactly.
 */
/**
 * The summary rows under the table, and the width of each line column.
 *
 * Both are per DOCUMENT TYPE for the same reason the visible columns are: a
 * sales order printed as a picking list and the invoice that follows it are
 * different papers, and a choice made on one must not silently reshape the
 * other.
 *
 * Widths are whole percents. They deliberately need not add to 100 —
 * table-layout:fixed normalises whatever it is given, which is also what makes
 * hiding a column safe — so the operator can widen one without arithmetic.
 */
const ROW_KEYS = ['status', 'subtotal', 'paid'] as const;
type RowKey = (typeof ROW_KEYS)[number];
const ALL_ROWS: Record<RowKey, boolean> = { status: true, subtotal: true, paid: true };
const rowsKeyFor = (docType: string): string => `erp-doc-rows:${docType}`;
function loadRows(docType: string): Record<RowKey, boolean> {
  const hidden = loadPref(rowsKeyFor(docType), '').split(',');
  const out = { ...ALL_ROWS };
  for (const k of ROW_KEYS) if (hidden.includes(k)) out[k] = false;
  return out;
}
const widthsKeyFor = (docType: string): string => `erp-doc-widths:${docType}`;
/**
 * The product column is width-adjustable but never hideable — a line without a
 * product is not a line — so it carries a width key of its own alongside the
 * toggleable ones. An absent entry means "leave it to the stylesheet", which is
 * what every column wants until somebody says otherwise.
 */
type WidthKey = ColKey | 'name';
const WIDTH_KEYS: readonly WidthKey[] = ['name', ...COL_KEYS];
type Widths = Partial<Record<WidthKey, string>>;
function loadWidths(docType: string): Widths {
  try {
    const raw = loadPref(widthsKeyFor(docType), '');
    if (!raw) return {};
    return Object.fromEntries(
      raw.split(',').map((p) => p.split(':')).filter(([k, v]) => WIDTH_KEYS.includes(k as WidthKey) && v),
    ) as Widths;
  } catch { return {}; }
}
function saveWidths(docType: string, w: Widths): void {
  savePref(widthsKeyFor(docType), WIDTH_KEYS.filter((k) => w[k]).map((k) => `${k}:${w[k]}`).join(','));
}

const FONT_KEY = 'erp-doc-font';
const TYPE_KEY = 'erp-doc-type';
/** Class suffix → the stylesheet holds the actual stack. '' is the original. */
const FONTS = ['', 'f-sans', 'f-system', 'f-serif'] as const;
const TYPE_STEPS = ['0.85', '0.925', '1', '1.075', '1.15'] as const;

const HEADTINT_KEY = 'erp-doc-headtint';
function loadHeadTint(): boolean {
  try { return window.localStorage.getItem(HEADTINT_KEY) !== '0'; } catch { return true; }
}
function saveHeadTint(on: boolean): void {
  try { window.localStorage.setItem(HEADTINT_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

/** Per-document-type configuration; everything else is shared. */
export interface DocPrintConfig {
  /** API resource collection, e.g. '/invoices' or '/bills'. */
  endpoint: string;
  /** Detail-page path to return to, e.g. '/sales/invoices' or '/purchasing/bills'. */
  backPath: string;
  /** i18n key (documents ns) for the document title, e.g. 'invoices.documentTitle'. */
  titleKey: string;
  /** i18n key for the party-block label, e.g. 'invoices.printMeta.billTo'. */
  partyLabelKey: string;
  /** i18n prefix for the status watermark label, e.g. 'invoices.status'. */
  statusPrefix: string;
  /**
   * API collection that mints this document's public, no-login share link
   * (`GET {publicLinkEndpoint}/{id}/public-link` → `{ token, url, qrSvg }`).
   * Set it and the paper carries a real, scannable QR of that URL; leave it
   * undefined and no QR is printed at all.
   *
   * CUSTOMER-facing documents only (invoice, online order). SUPPLIER documents
   * — bills and purchase orders — must never set this: their public link would
   * expose purchasing data (costs, suppliers) to anyone the code is shown to.
   * Deliberately an explicit opt-in rather than something inferred from
   * `endpoint`, so a new document type is silently private until someone
   * decides otherwise.
   */
  publicLinkEndpoint?: string;
  /**
   * Template namespace for the "send on WhatsApp" button beside Print. Set it
   * and staff can push this document's PUBLIC link (see `publicLinkEndpoint`)
   * to the customer's phone straight off the paper.
   *
   * Pointless without `publicLinkEndpoint` — the button only appears once the
   * link is minted, because a message whose {link} renders empty is worse than
   * no message. Supplier documents set neither.
   */
  whatsappDocType?: WhatsAppDocType;
}

interface DocLine {
  lineNo: number;
  itemId?: string | null;
  itemName?: string | null;
  /**
   * The item's name in each language it has one in (`{ en: 'Photo Paper…' }`),
   * as entered on the product's Translations tab. The PAPER picks: an English
   * invoice prints the English name. Absent for a product with no translation,
   * and for charge lines.
   */
  itemNameTranslations?: Record<string, string> | null;
  itemImageId?: string | null;
  /** The code that scans back to THIS line — the pack's when a pack was sold. */
  itemBarcode?: string | null;
  /** The item's own SKU — the الكود column. */
  itemCode?: string | null;
  /** Base units per pack for the unit sold; null/1 ⇒ sold as singles. */
  unitFactor?: string | null;
  /** The ITEM's own unit of measure (قطعة, دست…) — the fallback for `unitName`. */
  itemUom?: string | null;
  description: string | null;
  unitName: string | null;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  taxAmount: string;
  lineTotal: string;
}

interface DocDetail {
  id: string;
  docNumber: string;
  docDate: string;
  dueDate: string | null;
  partyId: string;
  status: string;
  currencyCode: string;
  subtotal: string;
  taxTotal: string;
  discountTotal?: string;
  couponCode?: string | null;
  total: string;
  amountPaid: string;
  notes: string | null;
  /**
   * The number the SUPPLIER printed on their own paper — bills only, and only
   * when the office typed it in. Every paper below renders it off its presence
   * alone: no other document type carries the column, so that is gate enough.
   */
  supplierInvoiceNo?: string | null;
  /**
   * A RETURN (credit note): the paper is titled as one and names the invoice it
   * returns. Only invoices carry these, so their presence is gate enough.
   */
  isCreditNote?: boolean | 0 | 1 | null;
  creditOfDocNumber?: string | null;
  /**
   * Paid in ANOTHER currency's notes at the till or the register — see
   * cashNotes.ts. The invoice read carries the columns; the public paper and the
   * register's sale carry the objects.
   */
  cashInCurrency?: string | null;
  cashInAmount?: string | null;
  cashBackCurrency?: string | null;
  cashBackAmount?: string | null;
  cashIn?: { currencyCode: string; amount: string } | null;
  cashBack?: { currencyCode: string; amount: string } | null;
  /**
   * The money arrived ONLINE through FIB (confirmed by FIB's API) — set for a
   * storefront order and for the invoice it became. The paper says so next to
   * the status, so nobody collects the total a second time at the door.
   */
  fibPaid?: boolean;
  lines: DocLine[];
}

/**
 * The document's public share link and its QR, as rendered by the API.
 *
 * The QR encodes the bare public URL and nothing else, so any phone camera
 * offers to open it straight away — that is the whole point of printing it.
 * The SVG is generated server-side (the API already ships `qrcode`, and an SVG
 * scales to the printer's DPI where a 96px canvas would print fuzzy), so the
 * page just injects the markup it is handed.
 */
interface PublicDocLink {
  /** Absolute, no-login URL — e.g. https://erp.vylo-tech.com/i/<token>. */
  url: string;
  /** Server-rendered QR of `url`, black on white with a quiet zone. */
  qrSvg: string;
}

/**
 * a4    — the full branded sheet (the shop's old paper, replicated).
 * a42   — the SAME sheet wearing a professional face: a structured meta
 *         strip, a dark table head, a highlighted grand total, the spelled-out
 *         amount, the share QR and signature lines. One tree, styled twice, so
 *         the two A4s can never disagree about the figures.
 * plain — the SAME sheet on A5 with everything the shop's pre-printed paper
 *         already carries (letterhead, seller block, footer, terms) switched
 *         off, and a blank top/bottom margin left for it.
 * roll  — 80mm thermal receipt.
 */
type Format = 'a4' | 'a42' | 'plain' | 'roll';

/**
 * The party fields the layout actually draws — narrower than the full `Party`
 * so the public DTO satisfies it too. The authed /parties response still
 * satisfies it structurally.
 *
 * The public DTO now sends `phone` (a delivery paper is not much use to a
 * driver without it) but still withholds `email`. Phone is not free of
 * consequence — `parties.phone` is also the storefront login id and a `/d/`
 * token never expires — so it is published deliberately, and the reasoning
 * lives with the field in public-document.controller.ts.
 */
interface PaperParty {
  displayName: string;
  code: string;
  billingAddress: Record<string, unknown> | null;
  email?: string | null;
  phone?: string | null;
  taxId: string | null;
}

/** Likewise for the seller letterhead — the subset of CmsSettings this page reads. */
interface PaperSeller {
  storeName: string | null;
  tagline: string | null;
  logoAttachmentId: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  socialLinks?: { website?: string | null } | null;
  address: Record<string, unknown> | null;
  slug: string;
}

/**
 * Either the authed viewer (a config + an `:id` route param) or the public one
 * (a share token and nothing else). A union, not two optional props, so the
 * compiler rejects both-or-neither and the public branch stays type-safe.
 */
type DocumentPrintPageProps =
  | { config: DocPrintConfig; publicToken?: undefined }
  | { publicToken: string; config?: undefined };

/** A4 at 96dpi. The paper's fixed width, and the divisor for the fit-to-width scale. */
const A4_WIDTH_PX = 794;
/** A5 portrait at 96dpi (148mm) — the "plain" sheet. Same 1px≈0.2646mm scale,
 *  so only the WIDTH changes: 12px type prints the same physical size on both. */
const A5_WIDTH_PX = 559;

/**
 * The sheet preferences are read by a dozen `useState` initialisers, so the
 * shop's saved defaults have to be in localStorage BEFORE this page mounts —
 * hence the gate: fetch once, seed what this browser has never chosen, then
 * render. The public copy skips it entirely; those are the staff's printing
 * preferences, not facts about the document.
 */
export function DocumentPrintPage(props: DocumentPrintPageProps): JSX.Element {
  const [seeded, setSeeded] = useState(!!props.publicToken);
  useEffect(() => {
    if (props.publicToken) return undefined;
    let cancelled = false;
    void api()
      .get<{ items: Array<{ key: string; value: unknown }> }>('/settings')
      .then((r) => {
        const v = r.data.items.find((x) => x.key === DOC_PREFS_SETTING)?.value;
        if (!cancelled && v && typeof v === 'object') seedPrefs(v as Record<string, unknown>);
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setSeeded(true); });
    return () => { cancelled = true; };
  }, [props.publicToken]);
  if (!seeded) return <div className="docview" />;
  return <DocumentPrintSheet {...props} />;
}

function DocumentPrintSheet({ config, publicToken }: DocumentPrintPageProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('documents');
  const { t: tc } = useTranslation();
  const brand = getBrand();
  const isPublic = !!publicToken;
  const [invoice, setInvoice] = useState<DocDetail | null>(null);
  const [party, setParty] = useState<PaperParty | null>(null);
  const [tenant, setTenant] = useState<{ name: string; baseCurrencyCode: string } | null>(null);
  const [seller, setSeller] = useState<PaperSeller | null>(null);
  // Public mode only: which document type the token resolved to, so the layout
  // can pick the same config the staff page uses.
  const [publicDoc, setPublicDoc] = useState<PublicPaperDocument | null>(null);
  const [notFound, setNotFound] = useState(false);
  // A logo the browser could not load falls back to the initials block rather
  // than printing a broken-image icon on the customer's paper.
  const [logoFailed, setLogoFailed] = useState(false);
  // Seeded from ?format= so a bookmark (or the mandob app's link) can open
  // straight into a mode — but deliberately NOT persisted: a sticky "plain"
  // would silently print a header-less invoice onto blank paper next time.
  // Anything unrecognised falls back to the full A4 sheet.
  const [search] = useSearchParams();
  const [fmt, setFmt] = useState<Format>(() => {
    const q = search.get('format');
    // 'plain' is pre-printed-stationery mode and is not offered publicly, so a
    // hand-typed ?format=plain must not reach it through the back door either.
    if (q === 'plain') return publicToken ? 'a4' : 'plain';
    return q === 'roll' || q === 'a42' ? q : 'a4';
  });
  const plain = fmt === 'plain';
  const pro = fmt === 'a42';
  const [plainTop, setPlainTop] = useState(() => loadMm('top'));
  const [plainBottom, setPlainBottom] = useState(() => loadMm('bottom'));
  const [plainSide, setPlainSide] = useState(() => loadMm('side'));
  // Public share link + QR. null = this document has none (supplier doc, no
  // permission, nothing shareable) → the paper carries NO QR at all. Never a
  // decorative stand-in: a code that scans as nothing is worse than no code,
  // because someone will point a phone at it.
  const [publicLink, setPublicLink] = useState<PublicDocLink | null>(null);
  const [lineBarcodes, setLineBarcodes] = useState<boolean>(() => loadLineBarcodes());
  const [fxNoteOn, setFxNoteOn] = useState<boolean>(() => loadFxNote());
  const [docLang, setDocLang] = useState<DocLang>(() => loadDocLang());
  /**
   * READ THIS PAPER IN ANOTHER CURRENCY.
   *
   * Display only. The sale keeps the currency it was made in — nothing here
   * writes, and the stored document is untouched. A customer handed an IQD
   * invoice can read it in USD without the shop raising a second document.
   *
   * DELIBERATELY NOT REMEMBERED between documents, unlike the language beside
   * it. Language changes which words a number is printed next to; this changes
   * what the number MEANS. A sticky override would have a cashier quietly
   * handing out converted papers all afternoon without noticing, so every
   * document opens in the currency it was actually sold in and the other
   * currency is one tap away.
   */
  const [altCcy, setAltCcy] = useState<string | null>(null);
  const [fxRates, setFxRates] = useState<DisplayFxRate[]>([]);
  // Print-time sheet options. The PUBLIC copy starts from the defaults instead
  // of the stored ones: those are the SHOP's choices for the paper it prints,
  // and on a shared browser a customer would otherwise open their own invoice
  // with the staff member's "no prices, no photos" columns and no control here
  // to put them back.
  // Keyed on the config's own endpoint ('/invoices', '/bills', …) so the memory
  // is per document type. Narrowed on `config` rather than `publicToken`: the
  // union is discriminated by which of the two is present, and an empty-string
  // token would be falsy without meaning "staff".
  const [cols, setCols] = useState<DocCols>(() => (config ? loadCols(config.endpoint) : { ...ALL_COLS }));
  const [bold, setBold] = useState<boolean>(() => (publicToken ? false : loadBold()));
  // The customer's copy always carries the document's notes and always looks
  // like the shop's sheet: these are the staff's printing preferences, not
  // facts about the document, and the public page has no control to undo them.
  const [showNotes, setShowNotes] = useState<boolean>(() => (publicToken ? true : loadShowNotes()));
  const [headTint, setHeadTint] = useState<boolean>(() => (publicToken ? true : loadHeadTint()));
  // A5 sheet shape. All default to the mode as it was, so a shop printing onto
  // pre-printed stationery today sees no change until it asks for one.
  const [a5Head, setA5Head] = useState<boolean>(() => (publicToken ? false : loadPref(A5_HEAD_KEY, '0') === '1'));
  const [a5Foot, setA5Foot] = useState<boolean>(() => (publicToken ? false : loadPref(A5_FOOT_KEY, '0') === '1'));
  // Compactness is a LEVEL now, not a switch: 0 = the airy original, 1 =
  // compact (the default since 2026-08-21 — owner request: "fit more products
  // on one page"), 2 = extra compact (smaller line type too). Stored as the
  // same key — old '0'/'1' values read unchanged; a browser that has chosen
  // keeps its choice. The customer's public copy stays the airy sheet.
  const [denseLevel, setDenseLevel] = useState<number>(() => {
    if (publicToken) return 0;
    const n = Number(loadPref(DENSE_KEY, '1'));
    return Number.isFinite(n) ? Math.min(2, Math.max(0, Math.trunc(n))) : 1;
  });
  const dense = denseLevel >= 1;
  const ultra = denseLevel === 2;
  const [tint, setTint] = useState<string>(() => (publicToken ? DEFAULT_TINT : loadPref(TINT_KEY, DEFAULT_TINT)));
  const [font, setFont] = useState<string>(() => (publicToken ? '' : loadPref(FONT_KEY, '')));
  const [type, setType] = useState<string>(() => (publicToken ? '1' : loadPref(TYPE_KEY, '1')));
  const [rows, setRows] = useState(() => (config ? loadRows(config.endpoint) : { ...ALL_ROWS }));
  const [widths, setWidths] = useState(() => (config ? loadWidths(config.endpoint) : {}));
  // The note box. It starts as the document's OWN saved note (what a person
  // wrote on it before), so opening the invoice again shows it; typing changes
  // only this printout until "Save" writes it to the document. Never carried to
  // another document: it is seeded per document, below, and nowhere else.
  const [printNote, setPrintNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const seededNoteFor = useRef<string | null>(null);
  const canSavePrefs = usePermission('settings.write') && !publicToken;
  const [savingPrefs, setSavingPrefs] = useState(false);
  /** Publish this browser's sheet preferences as the shop's starting point. */
  const saveShopPrefs = async (): Promise<void> => {
    setSavingPrefs(true);
    try {
      await api().put(`/settings/${DOC_PREFS_SETTING}`, { value: collectPrefs() });
      toast.success(t('invoices.options.defaultsSaved', { defaultValue: 'Saved as the shop default.' }));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSavingPrefs(false);
    }
  };
  // Open by default in the rail, shut by default in the bar. The rail exists to
  // hold these; collapsed, it is a column of four buttons and a lot of nothing.
  // In the bar the same panel costs four wrapped rows off the top of the sheet,
  // so there it stays closed until asked for.
  const [optsOpen, setOptsOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1100,
  );
  // Fit-to-width plumbing (see the effect below): 1 = full size, < 1 = the A4
  // sheet scaled down to the viewport, with the wrapper carrying its new height.
  const fitRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(1);
  const [paperHeight, setPaperHeight] = useState(0);

  const endpoint = config?.endpoint;
  const noteSavePerm = endpoint ? NOTE_SAVE_PERMISSION[endpoint] : undefined;
  const canSaveNote = usePermission(noteSavePerm ?? 'none.never') && !publicToken && !!noteSavePerm;
  const savedNote = splitNote(invoice?.notes);
  // Seed the box ONCE per document with what was saved on it.
  useEffect(() => {
    if (!invoice || publicToken) return;
    const key = `${endpoint ?? ''}:${invoice.id}`;
    if (seededNoteFor.current === key) return;
    seededNoteFor.current = key;
    setPrintNote(splitNote(invoice.notes).user);
  }, [invoice, endpoint, publicToken]);
  const noteDirty = printNote.trim() !== savedNote.user.trim();
  const saveNote = async (): Promise<void> => {
    if (!invoice || !endpoint || savingNote) return;
    setSavingNote(true);
    try {
      const text = printNote.trim();
      const composed = [text, savedNote.system].filter(Boolean).join('\n');
      await api().patch(`${endpoint}/${invoice.id}/notes`, { notes: composed || null });
      setInvoice({ ...invoice, notes: composed || null });
      // A saved note is meant to be read: make sure the sheet prints the
      // document's own notes now.
      if (text) { setShowNotes(true); }
      toast.success(t('invoices.options.noteSaved', { defaultValue: 'Note saved on the document.' }));
    } catch {
      toast.error(t('invoices.options.noteSaveFailed', { defaultValue: 'Could not save the note.' }));
    } finally {
      setSavingNote(false);
    }
  };
  useEffect(() => {
    if (publicToken) {
      // ONE anonymous GET fills every slot the four authed reads fill.
      void getPublicDocument(publicToken)
        .then((p) => {
          // Register the backend's per-currency decimals BEFORE any money is
          // formatted: without this the same IQD total prints with 3 decimals
          // here and none in the app, off identical numbers.
          setCurrencyDecimals(p.currencyDecimals);
          setPublicDoc(p);
          // The rates ride along in the same anonymous GET — the customer's
          // copy has no token for a second, authed call.
          setFxRates(p.fxRates ?? []);
          // id/partyId are the AUTHED page's fetch keys. The public DTO ships no
          // internal ids and the layout only reads them behind `!isPublic`.
          setInvoice({ ...p.document, id: '', partyId: '' });
          setParty(p.party);
          setSeller(p.seller);
          setTenant({ name: p.tenantName, baseCurrencyCode: '' });
          const svg = p.qrSvg.trim();
          // The server ships the QR with the document, so the printed paper is
          // byte-identical to the staff copy without a second round trip.
          if (svg.startsWith('<svg')) setPublicLink({ url: p.publicUrl, qrSvg: svg });
        })
        .catch(() => setNotFound(true));
      return;
    }
    if (!id || !endpoint) return;
    void (async () => {
      let doc: DocDetail;
      try {
        doc = (await api().get<DocDetail>(`${endpoint}/${id}`)).data;
      } catch {
        setNotFound(true);
        return;
      }
      setInvoice(doc);
      // Everything after the document is best-effort and INDEPENDENT. This
      // loader used to await each in turn with nothing catching them, so the
      // first refusal — a keeper or cashier holds neither tenants.read nor
      // cms.read — threw out of it ("Uncaught (in promise)") and everything
      // after it, the logo and the shop's contact block included, never loaded.
      const own = useAuthStore.getState().tenant;
      await Promise.all([
        // No rate on file, one currency, or no currencies.read: no chips.
        api()
          .get<{ items: DisplayFxRate[] }>('/currencies/display-rates', { params: { base: doc.currencyCode } })
          .then((f) => setFxRates(f.data.items))
          .catch(() => setFxRates([])),
        // The customer; failing that, at least the name the document carries.
        doc.partyId
          ? api()
              .get<PaperParty>(`/parties/${doc.partyId}`)
              .then((p) => setParty(p.data))
              .catch(() => {
                const name = (doc as DocDetail & { partyName?: string | null }).partyName;
                setParty(name ? { displayName: name, code: '', billingAddress: null, taxId: null } : null);
              })
          : Promise.resolve(),
        // Shop name, base currency and letterhead in one read any signed-in
        // person may make — the same letterhead the customer's public copy
        // shows. The session's own tenant covers the name if even that fails.
        api()
          .get<{ name: string; baseCurrencyCode: string; seller: PaperSeller }>('/tenants/current/paper')
          .then((tn) => {
            setTenant({ name: tn.data.name, baseCurrencyCode: tn.data.baseCurrencyCode });
            setSeller(tn.data.seller);
          })
          .catch(() => {
            setTenant({ name: own?.name ?? '', baseCurrencyCode: own?.baseCurrencyCode ?? doc.currencyCode });
            setSeller(null);
          }),
      ]);
    })();
  }, [id, endpoint, publicToken]);

  // Mint the public, no-login link for the QR — a separate, best-effort request
  // so a missing route / missing permission never blocks the document itself.
  const mintEndpoint = config?.publicLinkEndpoint;
  useEffect(() => {
    // The public paper IS the mint target: the server already sent its own QR
    // with the document, and minting needs a session this page does not have.
    if (isPublic) return;
    setPublicLink(null);
    if (!id || !mintEndpoint) return;
    void api().get<Partial<PublicDocLink>>(`${mintEndpoint}/${id}/public-link`)
      .then((r) => {
        const svg = r.data.qrSvg?.trim();
        const url = r.data.url;
        // Print the code only when the server actually sent one to print.
        if (url && svg?.startsWith('<svg')) setPublicLink({ url, qrSvg: svg });
      })
      .catch(() => { /* not shareable → no QR */ });
  }, [id, mintEndpoint, isPublic]);

  // Fit-to-width. A4 is a fixed 794px and a phone viewport is ~390 — the page's
  // own `max-width:100%` squeezes the sheet until the items table is unreadable,
  // which is exactly how most people will open a WhatsApp link. Scale the whole
  // sheet down instead, and give the wrapper the SCALED height so the transform
  // (which does not affect layout) leaves no dead space under the paper.
  // Applies in both modes — staff on a tablet get it too. The divisor is the
  // sheet's OWN width: measuring the 559px A5 against 794 would shrink it a
  // second time on a phone, for no reason.
  const paperWidth = plain ? A5_WIDTH_PX : A4_WIDTH_PX;
  useEffect(() => {
    const box = fitRef.current;
    const page = pageRef.current;
    if (!box || !page) return;
    const measure = (): void => {
      // The wrapper is capped at `paperWidth`, so this ratio is never above 1.
      const f = Math.min(1, box.clientWidth / paperWidth);
      setFit(f);
      // offsetHeight is the UNtransformed height, so this never feeds back.
      setPaperHeight(page.offsetHeight);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    ro.observe(page);
    measure();
    return () => ro.disconnect();
  }, [fmt, invoice, paperWidth]);

  // Public media URL (works in <img>, no auth header) for a tenant attachment.
  const mediaUrl = (attachmentId?: string | null): string | null =>
    attachmentId && seller?.slug ? assetUrl(`/api/v1/storefront/${seller.slug}/media/${attachmentId}`) : null;

  // `locale` is the APP's — it drives the viewer chrome and the WhatsApp draft.
  // The paper below has its own (`docLocale`), which is the whole point of the
  // language switch: printing an English invoice must not flip the dashboard.
  const locale = i18n.language;
  const isAr = i18n.dir(locale) === 'rtl';
  const bilingual = docLang === 'both';
  // '' (nobody has chosen) follows the app, which is the behaviour that existed
  // before this control did. In bilingual mode the app still decides which of
  // the two languages leads, so nobody is handed a paper in an order they did
  // not expect.
  const docAr = docLang === 'ar' || ((docLang === '' || bilingual) && isAr);
  const docLocale = docAr ? 'ar' : 'en';
  const docDir = docAr ? 'rtl' : 'ltr';

  // A dead or tampered share token — the only failure this page can render.
  if (notFound) {
    return (
      <div className="docview" dir={i18n.dir(locale)}>
        <style>{CSS}</style>
        <div className="dv-stage"><p className="dv-empty">{t('invoices.publicNotFound')}</p></div>
      </div>
    );
  }

  // Public mode has no route config: resolve the staff page's own config from
  // the docType the server returned, so the two copies cannot drift.
  const cfg = config ?? (publicDoc ? DOC_PRINT_CONFIGS[publicDoc.docType] : null);
  if (!cfg || !invoice) {
    return <p className="p-6 text-sm text-fg-muted">{tc('actions.loading')}</p>;
  }

  // Paper translators, pinned to the chosen document language rather than the
  // app's. `getFixedT` reads the already-bundled catalog for that language and
  // changes nothing globally — switching the paper to English leaves the
  // dashboard around it in Arabic.
  const fixedT = (lng: string): ((k: string, d?: string) => string) => {
    const f = i18n.getFixedT(lng, 'documents');
    return (k, d) => f(k, { defaultValue: d ?? k }) as unknown as string;
  };
  const tPrimary = fixedT(docLocale);
  const tAlt = bilingual ? fixedT(docAr ? 'en' : 'ar') : null;
  /** A paper label as a plain string — for the primary language only. */
  const td = (k: string, d?: string): string => tPrimary(k, d);
  /** A paper label as JSX, carrying the second language in bilingual mode. */
  const bi = (k: string, d?: string): JSX.Element =>
    <Bi a={tPrimary(k, d)} b={tAlt ? tAlt(k, d) : null} />;

  /**
   * The currency this SHEET is written in, which is the document's own unless
   * someone has asked to read it in another. `fx` is null whenever they agree,
   * and every conversion below is a no-op in that case — the untouched path
   * stays byte-identical to what it printed before this control existed.
   */
  const fx = altCcy && altCcy !== invoice.currencyCode
    ? fxRates.find((r) => r.code === altCcy) ?? null
    : null;
  const paperCcy = fx ? fx.code : invoice.currencyCode;
  const fxMul = fx ? Number(fx.rate) : 1;
  /**
   * Every figure on the paper is converted from the STORED amount, never from
   * another converted figure. Rounding then cannot compound down a column, and
   * each number is individually a faithful conversion of the one it replaces.
   */
  const cv = (v: string | number): number => Number(v) * fxMul;

  const m = (amount: string | number): string =>
    fx
      ? formatMoney(cv(amount).toFixed(4), { locale: docLocale, currency: paperCcy })
      : formatMoney(typeof amount === 'number' ? amount.toFixed(4) : amount, { locale: docLocale, currency: invoice.currencyCode });
  // Latin digits are PINNED on this page: the printout leaves the app and is read
  // (and re-keyed) by the customer, so it must not follow the staff user's personal
  // numerals toggle. The money beside these figures is Latin for the same reason.
  const n = (value: string | number): string => formatNumber(value, { locale: docLocale, numberingSystem: 'latn' });
  /**
   * What a line is CALLED on this paper.
   *
   * Product names used to print exactly as typed, whatever language the sheet
   * was in — so an English invoice listed «ورق فوتو 250 غرام». The shop already
   * types the other name on the product's Translations tab; this is where it
   * finally reaches the customer. No translation for the chosen language falls
   * back to the name as entered, which is still the truest thing available.
   */
  const lineName = (l: DocLine): string =>
    l.itemNameTranslations?.[docLocale]?.trim() || l.itemName || l.description || '—';

  // The old shop system's number faces: every TOTAL prints the currency's own
  // decimals (IQD → "360,000") — bare numbers, no currency symbol, Latin digits.
  // A LINE prints cents only when it has some: "51,000" not "51,000.00", but
  // "1.75" stays (owner, 2026-11-01 — Abdulsattar circled every ".00").
  const grp = (v: number, dec: number): string =>
    v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const decOf = (code: string): number => getCurrencyDecimals(code) ?? (() => {
    // Registry not loaded (this page has no app shell) — CLDR knows IQD is 0.
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits ?? 2; }
    catch { return 2; }
  })();
  // Decimals follow the currency being SHOWN: converting an IQD paper to USD
  // that still printed whole units would round every cent away.
  const totDec = decOf(paperCcy);
  const mLine = (v: string | number): string => {
    const x = cv(v);
    return Math.abs(x - Math.round(x)) < 0.005 ? grp(Math.round(x), 0) : grp(x, 2);
  };
  const mTotal = (v: string | number): string => grp(cv(v), totDec);
  // The old paper's own date face: dd-mm-yyyy, Latin digits, no locale variance.
  const dmy = (iso: string): string => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };
  const paperDate = dmy(invoice.docDate);

  /**
   * THE LINE THAT KEEPS A CONVERTED PAPER HONEST.
   *
   * Without it a sheet reading "USD 274.81" is indistinguishable from a sale
   * actually made in dollars, and the shop has handed out a document that
   * misstates what was agreed. So a converted paper always carries the rate, the
   * date the rate is from, and — the important part — THE ORIGINAL AMOUNT IN THE
   * ORIGINAL CURRENCY. The contractual figure never leaves the page.
   *
   * Printed in every layout including `plain`, which strips almost everything
   * else: pre-printed stationery is no reason to omit the one line that says
   * these numbers are not the ones the customer agreed to. Rendered only when
   * actually converting, so an ordinary print is untouched.
   */
  const fxNote: string | null = (() => {
    if (!fx || !fxNoteOn) return null;
    // Say the rate the way a person does — whichever direction reads above one.
    const flip = fxMul < 1;
    const shown = flip ? 1 / fxMul : fxMul;
    // Isolated left-to-right: dropped into an Arabic sentence, "1 USD = 1,700 IQD"
    // otherwise has its runs reordered and prints as "1 IQD = 1,700 USD".
    const ltr = (s: string): string => `\u2066${s}\u2069`;
    const rate = ltr(`1 ${flip ? paperCcy : invoice.currencyCode} = ${grp(shown, shown >= 100 ? 0 : 4)} ${flip ? invoice.currencyCode : paperCcy}`);
    return i18n.getFixedT(docLocale, 'documents')('invoices.docCcy.note', {
      defaultValue: 'Shown in {{ccy}} for reading only, at {{rate}} ({{date}}). This document was issued in {{base}} {{amount}}.',
      ccy: ltr(paperCcy),
      rate,
      date: ltr(dmy(fx.asOf)),
      base: ltr(invoice.currencyCode),
      amount: ltr(grp(Number(invoice.total), decOf(invoice.currencyCode))),
    }) as unknown as string;
  })();

  const totalNum = Number(invoice.total);
  // A sales ORDER has no `amount_paid` column at all — payment happens against
  // the invoice it becomes — so this is undefined there and printed literally
  // as "NaN" on the customer's paper. Anything not a real number means the
  // document simply does not carry the fact, and the row is left off.
  const paidNum = Number(invoice.amountPaid);
  const hasPaid = Number.isFinite(paidNum);
  const dueNum = totalNum - (hasPaid ? paidNum : 0);
  // The dinars a dollar invoice was really paid in (or the reverse). Facts in
  // their own currency: the reading-currency switch never converts them.
  const cash = cashNotes(invoice);
  const cashAmt = (v: number, ccy: string): string => `${grp(v, decOf(ccy))} ${ccy}`;
  // Show tax only when the doc actually carries tax (hidden when the tax module
  // is off → taxTotal=0; still printed for historical taxed docs).
  const hasTax = Number(invoice.taxTotal) > 0;

  // Charge lines (no item — e.g. the delivery fee) print as their own totals rows,
  // not as product line items. Products stay in the items table.
  const productLines = invoice.lines.filter((l) => l.itemId != null);
  // A5 fits itself to the invoice: a short one keeps the chosen spacing, a long
  // one tightens its rows by itself (and a very long one its type) so it still
  // lands on as few pages as it can. Nobody has to find the Compact slider first.
  const autoDense = plain && productLines.length > 10;
  const autoUltra = plain && productLines.length > 16;
  const chargeLines = invoice.lines.filter((l) => l.itemId == null);
  const chargesPreTax = chargeLines.reduce((s, l) => s + Number(l.quantity) * Number(l.unitPrice), 0);
  const productsSubtotal = Number(invoice.subtotal) - chargesPreTax;

  // The old sheet carries no rotated watermark — the status prints as a totals
  // row (حالة البيع) instead, which is where its previous system said it.
  // A FIB-confirmed online payment rides along on the SAME label — one string,
  // so every layout that prints the status (modern header, plain head, classic
  // totals row) says "paid via FIB" without three separate render sites.
  const statusBase = td(`${cfg.statusPrefix}.${invoice.status}`, invoice.status);
  const statusLabel = invoice.fibPaid
    ? `${statusBase} · ${td('invoices.printMeta.fibPaid', 'Paid via FIB')}`
    : statusBase;

  const sellerName = seller?.storeName?.trim() || tenant?.name || brand.name.full;
  const sellerSub = seller?.tagline?.trim() || (docAr ? brand.name.tagline_ar : brand.name.tagline_en);
  const sellerLogo = logoFailed ? null : mediaUrl(seller?.logoAttachmentId);
  const sellerPhone = seller?.contactPhone?.trim() || '';
  // The pro letterhead's contact lines — the shop's OWN words only (no platform
  // tagline fallback: a letterhead that names the software is not the shop's).
  const sellerTagline = seller?.tagline?.trim() || '';
  const sellerEmail = seller?.contactEmail?.trim() || '';
  const sellerWeb = (seller?.socialLinks?.website ?? '').trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
  const sellerAddr = addressText(seller?.address);
  const addrLine = addressText(party?.billingAddress);

  // `chromeTitle` is the viewer's own heading and stays in the app's language;
  // everything with `doc`/`paper` in the name below belongs to the sheet.
  // A credit note is printed as what it is to the shop — a return — not as a sale.
  const titleKey = invoice.isCreditNote ? 'invoices.returnTitle' : cfg.titleKey;
  const chromeTitle = t(titleKey);
  const docTitle = td(titleKey);
  const qrCaption = td('invoices.printMeta.qrCaption', 'Scan to view');

  // The document number as a scannable Code 128 — RECEIPT ONLY, so a sale can be
  // pulled up at the counter for a return or a reprint without anyone typing the
  // number. The A4 sheet carries none, matching the shop's own invoice. Wide
  // modules because thermal paper prints coarsely and a hairline smears into its
  // neighbour. `code128Svg` returns null for a number Code 128 cannot carry (an
  // Arabic series, say) — then the receipt simply prints without one.
  const barcodeRoll = code128Svg(invoice.docNumber, {
    moduleWidth: 1.6, height: 44, fontSize: 11, quietModules: 8, background: '',
  });

  // A line's own barcode NUMBER — the pack's when a pack was sold, else the
  // product's. Printed as digits, not bars: the document already carries one
  // scannable code, and a column of barcodes per line buries the invoice under
  // its own ink. The number is what someone actually types or reads out.
  // Charge lines (delivery, fees) carry no item and get nothing.
  const lineCode = (l: DocLine): string | null =>
    lineBarcodes && l.itemBarcode ? l.itemBarcode : null;

  // A column's chosen width, or nothing — an absent entry leaves the width to
  // the stylesheet, which is what all but the product column want. The product
  // column reads its stored width under the key 'name' because that is what the
  // operator is choosing; its class is the older `.l`.
  const w = (k: WidthKey): CSSProperties | undefined => (widths[k] ? { width: `${widths[k]}%` } : undefined);
  const setWidth = (k: WidthKey, raw: string): void => {
    const next: Widths = { ...widths };
    const v = raw.trim();
    if (!v) delete next[k];
    else next[k] = String(Math.min(80, Math.max(2, Math.round(Number(v) || 0))));
    setWidths(next);
    if (config) saveWidths(config.endpoint, next);
  };

  const setCol = (k: ColKey, on: boolean): void => {
    const next = { ...cols, [k]: on };
    setCols(next);
    if (config) saveCols(config.endpoint, next);
  };
  // The toolbar's column switches read the paper's OWN column headings, in the
  // APP's language — the operator picks columns in the language they work in,
  // whatever language the sheet is being printed in. Two are only offered where
  // they mean something: tax on a document that carries tax, and the photo
  // anywhere but A5 plain, which drops photos regardless (they are ink, and
  // that mode prints data onto the shop's own paper).
  const columnToggles = ([
    ['img', 'invoices.printMeta.lineImage', 'Image'],
    ['code', 'invoices.printMeta.lineCode', 'Code'],
    ['pack', 'invoices.printMeta.linePack', 'Pack'],
    ['unit', 'invoices.printMeta.lineUnit', 'Unit'],
    ['qty', 'invoices.printMeta.lineQty', 'Qty'],
    ['price', 'invoices.printMeta.lineUnitPrice', 'Unit price'],
    ['tax', 'invoices.printMeta.lineTaxPct', 'Tax %'],
    ['total', 'invoices.printMeta.lineTotal', 'Total'],
  ] as const).filter(([k]) => (k !== 'tax' || hasTax) && (k !== 'img' || !plain));
  // The three plain-mode millimetre values reach the SCREEN preview as custom
  // properties (the printed page gets them as an @page margin instead), so the
  // operator sees the same blank bands the printer will leave.
  const rootStyle = {
    // The chosen wash rides in as a property so one rule serves both sheets and
    // the default keeps the A4 byte-identical to the sheet it was copied from.
    '--head-tint': `var(${tint})`,
    '--type': type,
    '--plain-top': `${plainTop}mm`,
    '--plain-bottom': `${plainBottom}mm`,
    '--plain-side': `${plainSide}mm`,
  } as CSSProperties;
  // Both values ride in as custom properties so the @media print block can reset
  // them by cascade — an inline `height` would need !important to override.
  const fitStyle = {
    '--paper-fit': String(fit),
    '--paper-fit-h': fit < 1 && paperHeight > 0 ? `${Math.round(paperHeight * fit)}px` : 'auto',
    '--paper-w': `${paperWidth}px`,
  } as CSSProperties;

  return (
    <div className="docview" dir={i18n.dir(locale)} style={rootStyle}>
      <style>{CSS}</style>
      {/* 3-value margin shorthand = top / sides / bottom. In plain mode the blank
          space HAS to be a page margin rather than padding on the paper div:
          their letterhead and footer are pre-printed on EVERY sheet, and padding
          would only reserve the gap on page 1. As a margin, Chrome also repeats
          the items <thead> under it on page 2 for free. */}
      {/* A4 prints with real page margins (the old system relied on the browser's
          default ~12.7mm) — so page 2+ of a long invoice starts a margin below
          the paper edge, with the repeated <thead> under it, exactly like the
          old printouts. The sheet's own screen padding is zeroed in print (see
          @media print) so the margin isn't doubled on page 1. */}
      <style media="print">{
        fmt === 'roll' ? '@page{size:80mm auto;margin:0}'
          : plain ? `@page{size:A5;margin:${plainTop}mm ${plainSide}mm ${plainBottom}mm}`
            // Compact narrows the page margin too — 8mm (6mm on extra) clears
            // every printer's hardware edge, and on a dense sheet each spared
            // millimetre is part of a row.
            : `@page{size:A4;margin:${ultra ? '6mm' : dense ? '8mm' : '12.7mm'}}`
              // The professional sheet pins its foot to the page bottom: as tall
              // as the printable page (A4 less both margins, a hair under so a
              // one-page invoice never spills a blank second sheet).
              + (pro ? `.page.pro{min-height:calc(297mm - ${ultra ? 12 : dense ? 16 : 25.4}mm - 2mm);overflow:visible}` : '')
      }</style>

      {/* viewer chrome. On the public paper the two per-STAFF-USER preferences
          (document colour, item barcodes) and the back link are hidden: there
          is no app to go back to, the link would leak cfg.backPath, and neither
          preference is the customer's to change. `lineBarcodes` still defaults
          to true out of an empty localStorage, so the printed sheet is
          identical either way. The format toggle and Print stay — a customer
          opening this on a phone is exactly who needs them. */}
      <header className="dv-bar">
        {!isPublic && (
          <Link to={`${cfg.backPath}/${invoice.id}`} className="dv-back">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            <span>{tc('actions.back', { defaultValue: 'Back' })}</span>
          </Link>
        )}
        <span className="dv-title">{chromeTitle} · <span className="font-mono">{invoice.docNumber}</span></span>
        <span className="dv-spacer" />
        {/* Paper language. Shown on the PUBLIC copy too, unlike the two staff
            preferences below: which language a customer reads their own invoice
            in is theirs to pick, and they are the one holding the phone. The
            labels are written in the language they select, not translated — a
            language picker nobody can read is no picker at all. */}
        <div className="dv-seg" role="group" aria-label={t('invoices.docLang.label', { defaultValue: 'Document language' })}>
          <button type="button" aria-pressed={docAr && !bilingual} onClick={() => { setDocLang('ar'); saveDocLang('ar'); }}>
            العربية
          </button>
          <button type="button" aria-pressed={!docAr && !bilingual} onClick={() => { setDocLang('en'); saveDocLang('en'); }}>
            English
          </button>
          <button type="button" aria-pressed={bilingual} onClick={() => { setDocLang('both'); saveDocLang('both'); }}>
            AR+EN
          </button>
        </div>
        {/* Read this paper in another currency. Shown on the PUBLIC copy for the
            same reason the language is: the customer holding the phone is the
            one who wants the answer, and asking the shop to reissue an invoice
            just to read it in dollars is not a thing anyone should have to do.
            Absent entirely for a shop with one currency or no rate on file —
            there is nothing to offer. */}
        {fxRates.length > 0 && (
          <div className="dv-seg" role="group" aria-label={t('invoices.docCcy.label', { defaultValue: 'Currency shown' })}>
            <button
              type="button"
              aria-pressed={!fx}
              onClick={() => setAltCcy(null)}
              title={t('invoices.docCcy.ownHint', { defaultValue: 'The currency this document was issued in' })}
            >
              {invoice.currencyCode}
            </button>
            {fxRates.map((r) => (
              <button
                key={r.code}
                type="button"
                aria-pressed={fx?.code === r.code}
                onClick={() => setAltCcy(r.code)}
                title={t('invoices.docCcy.altHint', { defaultValue: 'Converted for reading only — the sale stays in its own currency' })}
              >
                {r.code}
              </button>
            ))}
          </div>
        )}
        {/* Only while actually converting — an unconverted sheet has no note to
            hide. Staff only: on the customer's own copy the explanation stays,
            because they are the person it protects. */}
        {!isPublic && fx && (
          <label className="dv-check">
            <input
              type="checkbox"
              checked={fxNoteOn}
              onChange={(e) => { setFxNoteOn(e.target.checked); saveFxNote(e.target.checked); }}
            />
            <span>{t('invoices.docCcy.showNote', { defaultValue: 'Conversion note' })}</span>
          </label>
        )}
        {!isPublic && (
          <label className="dv-check">
            <input
              type="checkbox"
              checked={lineBarcodes}
              onChange={(e) => { setLineBarcodes(e.target.checked); saveLineBarcodes(e.target.checked); }}
            />
            <span>{t('invoices.lineBarcodes', { defaultValue: 'Item barcodes' })}</span>
          </label>
        )}
        {/* Columns, bold and the printout note live behind one disclosure: eight
            column switches inline would wrap the bar into three rows before
            anyone has opened them. Staff only — see the panel itself. */}
        {!isPublic && (
          <button
            type="button"
            className="dv-opt-btn"
            aria-expanded={optsOpen}
            onClick={() => setOptsOpen((o) => !o)}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden />
            <span>{t('invoices.options.label', { defaultValue: 'Sheet options' })}</span>
          </button>
        )}
        <div className="dv-seg" role="group" aria-label={t('invoices.format.label', { defaultValue: 'Format' })}>
          <button type="button" aria-pressed={fmt === 'a4'} onClick={() => setFmt('a4')}>
            {t('invoices.format.a4', { defaultValue: 'A4' })}
          </button>
          {/* The professional A4 — offered on the public copy too: it is the
              same document in a smarter suit, and the customer holding the
              phone may well prefer to save that one. */}
          <button
            type="button"
            aria-pressed={pro}
            onClick={() => setFmt('a42')}
            title={t('invoices.format.a42Hint', { defaultValue: 'A4 — professional layout' })}
          >
            {t('invoices.format.a42', { defaultValue: 'A42' })}
          </button>
          {/* A5 "plain" exists for the shop's PRE-PRINTED stationery — it blanks
              the letterhead, the footer, the QR and the signature line and
              leaves millimetre margins to line up with paper the customer does
              not have. On the copy the customer opens (in the app, or from the
              QR) it is an option that can only produce a worse document. */}
          {!isPublic && (
            /* Labelled just "A5". It was "A5 plain", meaning no letterhead — but
               the letterhead is a switch now, so the old label describes a state
               the sheet may not be in. The full sense lives on the tooltip, and
               a paper-size control reads better as paper sizes anyway. */
            <button
              type="button"
              aria-pressed={plain}
              onClick={() => setFmt('plain')}
              title={t('invoices.format.plainHint', { defaultValue: 'A5 — sized for pre-printed paper' })}
            >
              {t('invoices.format.plainShort', { defaultValue: 'A5' })}
            </button>
          )}
          <button type="button" aria-pressed={fmt === 'roll'} onClick={() => setFmt('roll')}>
            {t('invoices.format.receipt', { defaultValue: '80mm' })}
          </button>
        </div>
        {plain && (
          <div className="dv-mm" title={t('invoices.plain.hint')}>
            <span>{t('invoices.plain.label')}</span>
            {([['top', plainTop, setPlainTop], ['bottom', plainBottom, setPlainBottom], ['side', plainSide, setPlainSide]] as const).map(([k, v, set]) => (
              <label key={k}>
                <span>{t(`invoices.plain.${k}`)}</span>
                <input
                  type="number" min={0} max={PLAIN_MAX[k]} step={1} value={v}
                  onChange={(e) => {
                    // Clamp on the way IN, not on the way out: an unbounded value
                    // typed here becomes an @page margin, and a 500mm margin
                    // prints an empty sheet with no hint why.
                    const next = Math.min(PLAIN_MAX[k], Math.max(0, Math.round(Number(e.target.value) || 0)));
                    set(next); saveMm(k, next);
                  }}
                />
              </label>
            ))}
            <span>{t('invoices.plain.mm')}</span>
          </div>
        )}
        {/* The rail's foot. A wrapper so the two actions sink together whatever
            is above them — with the options panel closed there is no flexing
            sibling to push them down, and without this they would ride up under
            the last switch with a void beneath. In the top bar it is inert. */}
        <div className="dv-foot">
        {/* Send the customer the PUBLIC no-login link — the same URL the printed
            QR encodes — straight off the paper. Never on the public copy (the
            customer is already looking at it, and /whatsapp/config needs a
            session), and never before the link is minted: the template's {link}
            would render empty. */}
        {/* No longer gated on the customer HAVING a number: the send dialog lets
            the operator type one, so a missing or wrong number on the record is
            something they can fix at the moment of sending. */}
        {!isPublic && cfg.whatsappDocType && publicLink && (
          <WhatsAppButton
            className="dv-wa"
            size="sm"
            docType={cfg.whatsappDocType}
            phone={party?.phone ?? null}
            vars={{
              company: sellerName,
              customer: party?.displayName ?? '',
              docNumber: invoice.docNumber,
              date: formatDate(invoice.docDate, { locale }),
              dueDate: invoice.dueDate ? formatDate(invoice.dueDate, { locale }) : '',
              total: m(invoice.total),
              paid: hasPaid ? m(paidNum) : '',
              balance: m(dueNum),
              currency: paperCcy,
              link: publicLink.url,
            }}
          />
        )}
        {/* The shop's app lives at the domain root (on a custom domain the apex
            IS the download-the-app landing), so the public copy carries the way
            there: an invoice link forwarded on WhatsApp is many customers' first
            touch with the shop. Never on the staff view — staff are in the app. */}
        {isPublic && (
          <a
            className="dv-app"
            href={typeof window === 'undefined' ? '/' : `${window.location.origin}/`}
            target="_blank"
            rel="noreferrer"
          >
            <Smartphone className="h-4 w-4" aria-hidden />
            <span>{t('invoices.getApp', { defaultValue: 'Download the app' })}</span>
          </a>
        )}
        {/* The customer's own send. The staff button above needs a session and
            the recipient's phone — the public DTO withholds both — so this is a
            plain wa.me share of the page already open, with no recipient: the
            person holding the invoice picks who gets it. */}
        {isPublic && (
          <a
            className="dv-wa-share"
            href={`https://wa.me/?text=${encodeURIComponent(
              t('invoices.shareText', {
                defaultValue: '{{doc}} — {{company}}\n{{url}}',
                doc: invoice.docNumber,
                company: sellerName,
                url: typeof window === 'undefined' ? '' : window.location.href,
              }),
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            {t('invoices.sendWhatsApp', { defaultValue: 'Send on WhatsApp' })}
          </a>
        )}
        <button type="button" className="dv-print" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden />
          <span>{t('invoices.print', { defaultValue: 'Print / PDF' })}</span>
        </button>
        </div>

        {/* Inside the bar, not beside it: `.dv-bar` is the one element already
            hidden from the printer, so the panel cannot ever print itself.
            Everything in here is the SHOP's decision about its own paper, which
            is why none of it is offered on the customer's copy: a customer who
            can drop the price column or type a line onto what still looks like
            the shop's invoice can hand on a document the shop never issued. */}
        {!isPublic && optsOpen && (
          <div className="dv-opts">
            {/* Columns describe the A4/A5 line table; the 80mm receipt has no
                table to take them from. */}
            {fmt !== 'roll' && (
              <div className="grp">
                <span className="lbl">{t('invoices.options.columns', { defaultValue: 'Columns' })}</span>
                <label className="dv-check">
                  <span>{t('invoices.printMeta.lineProduct', { defaultValue: 'Product' })}</span>
                  <input
                    className="dv-w"
                    type="number" min={2} max={80} step={1}
                    value={widths.name ?? ''}
                    placeholder="%"
                    aria-label={t('invoices.printMeta.lineProduct', { defaultValue: 'Product' })}
                    onChange={(e) => setWidth('name', e.target.value)}
                  />
                </label>
                {columnToggles.map(([k, labelKey, fallback]) => (
                  <label className="dv-check" key={k}>
                    <input type="checkbox" checked={cols[k]} onChange={(e) => setCol(k, e.target.checked)} />
                    <span>{t(labelKey, { defaultValue: fallback })}</span>
                    {/* Its width, in percent, right where its name is — the two
                        questions about a column are asked in the same breath.
                        Blank leaves it to the table. */}
                    {cols[k] && (
                      <input
                        className="dv-w"
                        type="number"
                        min={2}
                        max={80}
                        step={1}
                        value={widths[k] ?? ''}
                        placeholder="%"
                        aria-label={`${t(labelKey, { defaultValue: fallback })} %`}
                        onClick={(e) => e.preventDefault()}
                        onChange={(e) => setWidth(k, e.target.value)}
                      />
                    )}
                  </label>
                ))}
              </div>
            )}
            <div className="grp">
              <label className="dv-check">
                <input
                  type="checkbox"
                  checked={bold}
                  onChange={(e) => { setBold(e.target.checked); saveBold(e.target.checked); }}
                />
                <span>{t('invoices.options.bold', { defaultValue: 'Bold text' })}</span>
              </label>
              <label className="dv-check">
                <input
                  type="checkbox"
                  checked={showNotes}
                  onChange={(e) => { setShowNotes(e.target.checked); saveShowNotes(e.target.checked); }}
                />
                <span>{t('invoices.options.showNotes', { defaultValue: "The document's notes" })}</span>
              </label>
              {/* Only where it changes anything: A4 is tinted regardless and the
                  80mm roll has no table head to tint. */}
              {plain && (
                <label className="dv-check">
                  <input
                    type="checkbox"
                    checked={headTint}
                    onChange={(e) => { setHeadTint(e.target.checked); saveHeadTint(e.target.checked); }}
                  />
                  <span>{t('invoices.options.headTint', { defaultValue: 'Coloured table head' })}</span>
                </label>
              )}
              {fmt !== 'roll' && (
                /* Compactness as a slider: three stops with the active level
                   named beside the track, so what the thumb means is never a
                   guess. */
                <label className="dv-row dv-slide">
                  <span>{t('invoices.options.dense', { defaultValue: 'Compact' })}</span>
                  <input
                    type="range"
                    min={0}
                    max={2}
                    step={1}
                    value={denseLevel}
                    aria-label={t('invoices.options.dense', { defaultValue: 'Compact' })}
                    onChange={(e) => {
                      const v = Math.min(2, Math.max(0, Number(e.target.value) || 0));
                      setDenseLevel(v); savePref(DENSE_KEY, String(v));
                    }}
                  />
                  <span className="dv-slide-val">
                    {t(`invoices.options.dense${denseLevel}`, {
                      defaultValue: ['Normal', 'Compact', 'Extra'][denseLevel],
                    })}
                  </span>
                </label>
              )}
            </div>

            {/* The A5 sheet's own shape. These exist because that mode was built
                to print onto the shop's pre-printed stationery — it drops the
                letterhead and the foot because the paper already carries them.
                A shop using plain A5 as an ordinary sheet wants them back, and
                only it knows which. */}
            {plain && (
              <div className="grp">
                <span className="lbl">{t('invoices.options.a5', { defaultValue: 'A5 sheet' })}</span>
                <label className="dv-check">
                  <input
                    type="checkbox"
                    checked={a5Head}
                    onChange={(e) => { setA5Head(e.target.checked); savePref(A5_HEAD_KEY, e.target.checked ? '1' : '0'); }}
                  />
                  <span>{t('invoices.options.a5Head', { defaultValue: 'Letterhead' })}</span>
                </label>
                <label className="dv-check">
                  <input
                    type="checkbox"
                    checked={a5Foot}
                    onChange={(e) => { setA5Foot(e.target.checked); savePref(A5_FOOT_KEY, e.target.checked ? '1' : '0'); }}
                  />
                  <span>{t('invoices.options.a5Foot', { defaultValue: 'Footer' })}</span>
                </label>
              </div>
            )}

            {/* The summary lines under the table. "Amount received" is absent on
                a sales order (that document has no payment against it), so it
                is offered only where it exists. */}
            <div className="grp">
              <span className="lbl">{t('invoices.options.rows', { defaultValue: 'Summary' })}</span>
              {(ROW_KEYS.filter((k) => k !== 'paid' || hasPaid)).map((k) => (
                <label className="dv-check" key={k}>
                  <input
                    type="checkbox"
                    checked={rows[k]}
                    onChange={(e) => {
                      const next = { ...rows, [k]: e.target.checked };
                      setRows(next);
                      if (config) savePref(rowsKeyFor(config.endpoint), ROW_KEYS.filter((r) => !next[r]).join(','));
                    }}
                  />
                  <span>{t(`invoices.options.row_${k}`, {
                    defaultValue: { status: 'Sale status', subtotal: 'Invoice total', paid: 'Amount received' }[k],
                  })}</span>
                </label>
              ))}
            </div>

            {/* Face and size. A select rather than a swatch row: unlike the
                colours these are named things with an order, and five buttons
                of identical grey would say nothing about which is bigger. */}
            <div className="grp">
              <span className="lbl">{t('invoices.options.type', { defaultValue: 'Type' })}</span>
              {/* Named in the open. Stacked in a rail, two bare dropdowns read as
                  "System" and "108%" with nothing saying which is which. */}
              <label className="dv-row">
                <span>{t('invoices.options.font', { defaultValue: 'Font' })}</span>
              <select
                className="dv-sel"
                value={font}
                onChange={(e) => { setFont(e.target.value); savePref(FONT_KEY, e.target.value); }}
                aria-label={t('invoices.options.font', { defaultValue: 'Font' })}
              >
                {FONTS.map((f) => (
                  <option key={f || 'default'} value={f}>
                    {t(`invoices.options.font_${f || 'default'}`, {
                      defaultValue: { '': 'Original', 'f-sans': 'Sans', 'f-system': 'System', 'f-serif': 'Serif' }[f],
                    })}
                  </option>
                ))}
              </select>
              </label>
              <label className="dv-row dv-slide">
                <span>{t('invoices.options.size', { defaultValue: 'Text size' })}</span>
                <input
                  type="range"
                  min={0}
                  max={TYPE_STEPS.length - 1}
                  step={1}
                  // An unknown stored value sits the thumb at 100%, which is
                  // what the sheet falls back to rendering anyway.
                  value={TYPE_STEPS.includes(type as (typeof TYPE_STEPS)[number]) ? TYPE_STEPS.indexOf(type as (typeof TYPE_STEPS)[number]) : 2}
                  aria-label={t('invoices.options.size', { defaultValue: 'Text size' })}
                  onChange={(e) => {
                    const v = TYPE_STEPS[Math.min(TYPE_STEPS.length - 1, Math.max(0, Number(e.target.value) || 0))]!;
                    setType(v); savePref(TYPE_KEY, v);
                  }}
                />
                <span className="dv-slide-val">{`${Math.round(Number(type) * 100)}%`}</span>
              </label>
            </div>

            {/* The wash behind the table heading, for a shop whose colour is not
                the blue this sheet was copied from. Swatches rather than a
                colour input: anything darker than these prints the heading
                unreadable, and a picker invites exactly that. */}
            {fmt !== 'roll' && (
              <div className="grp">
                <span className="lbl">{t('invoices.options.tint', { defaultValue: 'Head colour' })}</span>
                {TINTS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`dv-swatch${tint === c ? ' on' : ''}`}
                    style={{ background: `var(${c})` }}
                    onClick={() => { setTint(c); savePref(TINT_KEY, c); }}
                    aria-label={c}
                    aria-pressed={tint === c}
                    title={c}
                  />
                ))}
              </div>
            )}
            <div className="grp note">
              <label className="lbl" htmlFor="dv-print-note">
                {t('invoices.options.note', { defaultValue: 'Note on this printout' })}
              </label>
              <textarea
                id="dv-print-note"
                rows={2}
                value={printNote}
                onChange={(e) => setPrintNote(e.target.value)}
                placeholder={t('invoices.options.notePlaceholder', { defaultValue: 'e.g. goods received in good condition' })}
              />
              {canSaveNote && (
                <button type="button" className="dv-btn" disabled={!noteDirty || savingNote} onClick={() => void saveNote()}>
                  {savingNote
                    ? tc('actions.saving', { defaultValue: 'Saving…' })
                    : t('invoices.options.noteSave', { defaultValue: 'Save note on the document' })}
                </button>
              )}
              <span className="hint">
                {canSaveNote
                  ? t('invoices.options.noteHintSave', { defaultValue: 'Prints on this sheet. Press Save to keep it on the document, so it shows whenever the invoice is opened or printed again.' })
                  : t('invoices.options.noteHint', { defaultValue: 'Printed on this sheet only — it is not saved to the document and disappears when you leave this page.' })}
              </span>
            </div>
            {/* Everything above is remembered in THIS browser already. This
                writes the same set to the shop, so the next machine — the
                office PC, a new cashier's login — opens the sheet already
                looking the way this one does. */}
            {canSavePrefs && (
              <div className="grp">
                <span className="lbl">{t('invoices.options.defaults', { defaultValue: 'Shop defaults' })}</span>
                <button type="button" className="dv-btn" disabled={savingPrefs} onClick={() => void saveShopPrefs()}>
                  {savingPrefs
                    ? tc('actions.saving', { defaultValue: 'Saving…' })
                    : t('invoices.options.saveDefaults', { defaultValue: 'Save these settings for the shop' })}
                </button>
                <span className="hint">
                  {t('invoices.options.saveDefaultsHint', {
                    defaultValue: 'Applies to any browser that has not chosen its own — this one keeps what you set here.',
                  })}
                </span>
              </div>
            )}
          </div>
        )}
      </header>

      <div className="dv-stage">
        {fmt !== 'roll' ? (
          <div className="paper-fit" ref={fitRef} style={fitStyle}>
          {/* A5 "plain" is the SAME tree with pieces suppressed by CSS, not a
              second layout — one component, so the two can never drift. */}
          {/* The sheet carries its OWN dir and `.ar` class rather than inheriting
              the app's: with an Arabic dashboard printing an English invoice,
              an inherited `[dir=rtl]` would right-align the English paper. The
              class exists because the paper rules have to key off the DOCUMENT's
              language, and a bare `[dir=rtl] .stamp` still matches through the
              RTL wrapper above. */}
          <div
            className={`page${pro ? ' pro' : ''}${plain ? ' plain' : ''}${plain && headTint ? ' head-tint' : ''}${plain && a5Head ? ' a5head' : ''}${plain && a5Foot ? ' a5foot' : ''}${dense || autoDense ? ' dense' : ''}${ultra || autoUltra ? ' dense2' : ''}${font ? ` ${font}` : ''}${docAr ? ' ar' : ''}${bold ? ' bold' : ''}`}
            dir={docDir}
            ref={pageRef}
          >
            {/* Screen-only: show the operator the blank space they are reserving. */}
            {plain && plainTop > 0 && <div className="plain-reserve top" aria-hidden>{t('invoices.plain.reservedTop')}</div>}
            {plain && plainBottom > 0 && <div className="plain-reserve bottom" aria-hidden>{t('invoices.plain.reservedBottom')}</div>}

            {/* Classic letterhead, copied from the shop's previous system: the
                logo on the start side, the sheet's name in large type at the end
                side with the number and date stacked under it, then a full-width
                rule. Suppressed whole in plain mode — that IS their letterhead. */}
            <div className="inv-top">
              <div className="clsc-head">
                <div className="clsc-logo">
                  {sellerLogo ? (
                    <img className="logo" src={sellerLogo} alt={sellerName} onError={() => setLogoFailed(true)} />
                  ) : (
                    <div className="mk" aria-hidden>{(seller?.storeName ?? brand.name.short ?? 'IQ').slice(0, 2)}</div>
                  )}
                </div>
                {/* Pro face: who is selling, in full — the name, the shop's own
                    line, and how to reach it. The classic sheet leaves this to
                    the logo, as the shop's old paper did. */}
                {pro && (
                  <div className="pro-seller">
                    <div className="nm">{sellerName}</div>
                    {sellerTagline && <div className="tg">{sellerTagline}</div>}
                    {(sellerPhone || sellerEmail || sellerWeb) && (
                      <div className="ct">
                        {sellerPhone && <span><Tel>{sellerPhone}</Tel></span>}
                        {sellerEmail && <span dir="ltr">{sellerEmail}</span>}
                        {sellerWeb && <span dir="ltr">{sellerWeb}</span>}
                      </div>
                    )}
                    {sellerAddr && <div className="ad"><bdi>{sellerAddr}</bdi></div>}
                  </div>
                )}
                <div className="clsc-title">
                  <h1 className="dt">{bi(titleKey)}</h1>
                  <span><b>{td('invoices.printMeta.number')}: </b>{invoice.docNumber}</span>
                  {invoice.isCreditNote && invoice.creditOfDocNumber && (
                    <span><b>{td('invoices.printMeta.returnOf', 'Return of invoice')}: </b>
                      <span dir="ltr">{invoice.creditOfDocNumber}</span>
                    </span>
                  )}
                  {invoice.supplierInvoiceNo && (
                    <span><b>{td('invoices.printMeta.supplierInvoiceNo', 'Supplier invoice no.')}: </b>
                      {/* LTR: their number is a code, and an Arabic sheet would
                          otherwise reorder a mixed-script one like "INV-4471". */}
                      <span dir="ltr">{invoice.supplierInvoiceNo}</span>
                    </span>
                  )}
                  <span><b>{td('invoices.printMeta.date')}: </b>{paperDate}</span>
                </div>
              </div>
              <hr className="chr chr1" />
              {/* Pro face: the number/date lines above are hidden by CSS and the
                  same facts return as a structured strip — every cell a labelled
                  box, which is what makes the sheet read as designed rather than
                  typed. Keys all exist in both languages already. */}
              {pro && (
                <div className="pro-meta">
                  <div className="cell">
                    <span className="k">{bi('invoices.printMeta.number')}</span>
                    <span className="v" dir="ltr">{invoice.docNumber}</span>
                  </div>
                  {invoice.isCreditNote && invoice.creditOfDocNumber && (
                    <div className="cell">
                      <span className="k">{bi('invoices.printMeta.returnOf', 'Return of invoice')}</span>
                      <span className="v" dir="ltr">{invoice.creditOfDocNumber}</span>
                    </div>
                  )}
                  {invoice.supplierInvoiceNo && (
                    <div className="cell">
                      <span className="k">{bi('invoices.printMeta.supplierInvoiceNo', 'Supplier invoice no.')}</span>
                      <span className="v" dir="ltr">{invoice.supplierInvoiceNo}</span>
                    </div>
                  )}
                  <div className="cell">
                    <span className="k">{bi('invoices.printMeta.date')}</span>
                    <span className="v" dir="ltr">{paperDate}</span>
                  </div>
                  {invoice.dueDate && (
                    <div className="cell">
                      <span className="k">{bi('invoices.printMeta.dueDate')}</span>
                      <span className="v" dir="ltr">{dmy(invoice.dueDate)}</span>
                    </div>
                  )}
                  <div className="cell">
                    <span className="k">{bi('invoices.printMeta.currency')}</span>
                    <span className="v" dir="ltr">{paperCcy}</span>
                  </div>
                  <div className="cell">
                    <span className="k">{bi('invoices.printMeta.status')}</span>
                    <span className="v">{statusLabel}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="inv-body">
              {/* Recipient (bold, start side) opposite the issuer lines (muted,
                  end side) — the exact two blocks of the shop's old paper. */}
              <div className="clsc-info">
                <div className="clsc-cust">
                  {pro ? (
                    /* Pro: a labelled card — the name reads first and largest,
                       the contact lines under it, no "Label:" prefixes. */
                    <>
                      <div className="pro-k">{bi('invoices.printMeta.billTo', 'Bill to')}</div>
                      {party && <div className="pro-name">{party.displayName}</div>}
                      {party?.phone && <div className="pro-line"><Tel>{party.phone}</Tel></div>}
                      {addrLine && <div className="pro-line"><bdi>{addrLine}</bdi></div>}
                      {party?.taxId && <div className="pro-line">{td('invoices.printMeta.taxId')}: {party.taxId}</div>}
                    </>
                  ) : (
                    plain ? (
                      /* A5: the recipient's name, the date under it, then the
                         address, and the phone / tax id only when there is one,
                         so a walk-in's block is just a name and a date. */
                      <>
                        {party && <div><b>{td('invoices.printMeta.recipient', 'Recipient')}: {party.displayName}</b></div>}
                        <div><b>{td('invoices.printMeta.date')}: {paperDate}</b></div>
                        {addrLine && <div><b>{td('invoices.printMeta.address', 'Address')}: <bdi>{addrLine}</bdi></b></div>}
                        {party?.phone && <div><b>{td('invoices.printMeta.phone', 'Phone')}: <Tel>{party.phone}</Tel></b></div>}
                        {party?.taxId && <div><b>{td('invoices.printMeta.taxId')}: {party.taxId}</b></div>}
                      </>
                    ) : (
                      <>
                        {party && <div><b>{td('invoices.printMeta.recipient', 'Recipient')}: {party.displayName}</b></div>}
                        {party?.phone && <div><b>{td('invoices.printMeta.phone', 'Phone')}: <Tel>{party.phone}</Tel></b></div>}
                        {addrLine && <div><b>{td('invoices.printMeta.address', 'Address')}: <bdi>{addrLine}</bdi></b></div>}
                        {party?.taxId && <div><b>{td('invoices.printMeta.taxId')}: {party.taxId}</b></div>}
                      </>
                    )
                  )}
                </div>
                {/* Two lines: issuer and date. The warehouse line the old paper
                    carried is gone by request — it only ever echoed the shop's
                    name. The currency also used to print here; the A5 plain
                    head and the 80mm receipt still carry it, and this sheet's
                    figures are the shop's own money either way. */}
                <div className="clsc-meta">
                  <p>
                    {td('invoices.printMeta.issuedBy', 'Issued by')}: {sellerName}
                    {/* On A5 the date sits under the recipient's name instead, and
                        the document number takes the line under the issuer (by
                        request: nothing runs across the top of that sheet). */}
                    {!plain && <><br />{td('invoices.printMeta.date')}: {paperDate}</>}
                    {plain && !a5Head && <><br />{td('invoices.printMeta.number')}: <bdi>{invoice.docNumber}</bdi></>}
                    {plain && !a5Head && invoice.supplierInvoiceNo && (
                      <><br />{td('invoices.printMeta.supplierInvoiceNo', 'Supplier invoice no.')}: <bdi>{invoice.supplierInvoiceNo}</bdi></>
                    )}
                  </p>
                </div>
              </div>

              {/*
                Line items, in the column order of the shop's previous system:
                  ت. · الصورة · اسم المنتج · الكود · التعبئة · الوحدة · العدد · السعر · الإجمالي
                The picture, the code and the pack size get columns of their own rather
                than being stacked inside the description cell — this is a wholesale
                counter, where "which product, what pack, how many" is what a customer
                checks the paper for, and stacked text makes that a reading exercise.

                Any of them but the index and the product name can be switched off
                from the toolbar. Dropping a column takes its <th> AND its <td>s
                out together, so the table stays well-formed: the fixed layout
                shares the freed width among what is left, and the totals below
                are read off the document's own figures, never off these cells —
                so a hidden price column changes the paper, not the arithmetic.
              */}
              <table className="items">
                <thead>
                  <tr>
                    <th className="c-idx">{bi('invoices.printMeta.lineIndex', '#')}</th>
                    {cols.img && <th className="c-img" style={w('img')}>{bi('invoices.printMeta.lineImage', 'Image')}</th>}
                    <th className="l" style={w('name')}>{bi('invoices.printMeta.lineProduct', 'Product')}</th>
                    {cols.code && <th className="c-code" style={w('code')}>{bi('invoices.printMeta.lineCode', 'Code')}</th>}
                    {cols.pack && <th className="c-pack" style={w('pack')}>{bi('invoices.printMeta.linePack', 'Pack')}</th>}
                    {cols.unit && <th className="c-unit" style={w('unit')}>{bi('invoices.printMeta.lineUnit', 'Unit')}</th>}
                    {cols.qty && <th className="c-qty" style={w('qty')}>{bi('invoices.printMeta.lineQty')}</th>}
                    {cols.price && <th className="c-price" style={w('price')}>{bi('invoices.printMeta.lineUnitPrice')}</th>}
                    {hasTax && cols.tax && <th style={w('tax')}>{bi('invoices.printMeta.lineTaxPct', 'Tax %')}</th>}
                    {cols.total && <th className="c-total" style={w('total')}>{bi('invoices.printMeta.lineTotal')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {productLines.map((l, i) => {
                    const img = mediaUrl(l.itemImageId);
                    const title = lineName(l);
                    // A secondary line only when the description says something new.
                    const sub = descriptionAddsNothing(l, title) ? null : l.description;
                    // التعبئة and الوحدة are always stated, never dashed.
                    //
                    // A line sold in the base unit records unit_name NULL and
                    // unit_factor 1 — accurate, but it left both columns as em
                    // dashes on three of every four lines, which reads as missing
                    // data rather than as "singles". So: the pack is the factor,
                    // and 1 for a single, which is what it is; and the unit falls
                    // back from the packaging sold (Carton) to the item's own unit
                    // of measure (قطعة), which the API now sends. Only a charge
                    // line with no item behind it can still come up empty.
                    const pack = n(Number(l.unitFactor ?? '1') || 1);
                    // …and the unit reads in the PAPER's language: an English
                    // invoice says Piece / Carton, not قطعة / كرتون. A word the
                    // dictionary doesn't know prints as the shop typed it.
                    const unit = unitLabel(l.unitName ?? l.itemUom, docLocale) || '—';
                    return (
                      <tr key={l.lineNo}>
                        {/* COUNTED, not quoted. `lineNo` is the line's position in
                            the whole document, and the delivery fee (a charge line
                            with no item) is pulled out of this table into the
                            totals — so a fee sitting at line 2 printed the goods
                            as 1, 3, 4. A customer reading an invoice counts rows;
                            a gap reads as a missing item they were charged for. */}
                        <td className="idx">{i + 1}</td>
                        {cols.img && (
                          <td className="c-img">
                            <span className="thumb" aria-hidden>
                              {/* A picture that will not load leaves the cell empty, not a broken icon. */}
                              {img ? <img src={img} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
                            </span>
                          </td>
                        )}
                        <td className="l">
                          <span className="desc">{title}</span>
                          {sub && <span className="subdesc">{sub}</span>}
                        </td>
                        {cols.code && <td className="c-code">{l.itemCode ?? lineCode(l) ?? '—'}</td>}
                        {cols.pack && <td className="c-pack">{pack}</td>}
                        {cols.unit && <td className="c-unit">{unit}</td>}
                        {cols.qty && <td className="c-qty">{n(Number(l.quantity))}</td>}
                        {cols.price && <td className="c-price">{mLine(l.unitPrice)}</td>}
                        {hasTax && cols.tax && <td>{n(Number(l.taxRate) * 100)}%</td>}
                        {cols.total && <td className="c-total">{mLine(l.lineTotal)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Classic totals: a bare label/value list on the start side —
                  status, items total, discount when there is one, grand total,
                  amount received — exactly the rows of the shop's old paper.
                  Charges (delivery), tax and a remaining balance only appear
                  when they are actually non-zero, so the everyday sheet is
                  row-for-row the old one. */}
              <hr className="chr" />
              <table className="ctot">
                <tbody>
                  {rows.status && <tr><th>{td('invoices.printMeta.saleStatus', 'Sale status')}:</th><td>{statusLabel}</td></tr>}
                  {rows.subtotal && <tr><th>{td('invoices.printMeta.invoiceTotal', 'Invoice total')}:</th><td>{mTotal(productsSubtotal)}</td></tr>}
                  {chargeLines.map((l) => (
                    <tr key={l.lineNo}><th>{l.description ?? td('invoices.printMeta.delivery', 'Delivery')}:</th><td>{mTotal(l.lineTotal)}</td></tr>
                  ))}
                  {invoice.discountTotal !== undefined && Number(invoice.discountTotal) > 0 && (
                    <tr><th>{td('invoices.printMeta.invoiceDiscount', 'Invoice discount')}:</th><td>{mTotal(invoice.discountTotal)}{invoice.couponCode ? ` (${invoice.couponCode})` : ''}</td></tr>
                  )}
                  {hasTax && <tr><th>{td('invoices.printMeta.tax')}:</th><td>{mTotal(invoice.taxTotal)}</td></tr>}
                  <tr className="g"><th>{td('invoices.printMeta.grandTotal', 'Grand total')}:</th><td>{mTotal(invoice.total)}</td></tr>
                  {hasPaid && rows.paid && (
                    <tr><th>{td('invoices.printMeta.amountReceived', 'Amount received')}:</th><td>{mTotal(paidNum)}</td></tr>
                  )}
                  {cash && rows.paid && (
                    <>
                      <tr className="cash">
                        <th>{td(`invoices.printMeta.${paidLabelKey(cash.method)}`, PAID_LABEL_DEFAULT[paidLabelKey(cash.method)])}:</th>
                        <td dir="ltr">{cashAmt(cash.paid, cash.currency)}</td>
                      </tr>
                      {cash.equivalent !== null && (
                        <tr className="cash-sub">
                          <th>{td('invoices.printMeta.equivalent', 'i.e. the equivalent of')}:</th>
                          <td dir="ltr">{cashAmt(cash.equivalent, invoice.currencyCode)}</td>
                        </tr>
                      )}
                      {cash.handed !== null && (
                        <tr className="cash-sub">
                          <th>{td('invoices.printMeta.cashHanded', 'Cash handed over')}:</th>
                          <td dir="ltr">{cashAmt(cash.handed, cash.currency)}</td>
                        </tr>
                      )}
                      {cash.change && (
                        <tr className="cash-sub">
                          <th>{td('invoices.printMeta.change', 'Change')}:</th>
                          <td dir="ltr">{cashAmt(cash.change.amount, cash.change.currency)}</td>
                        </tr>
                      )}
                    </>
                  )}
                  {hasPaid && rows.paid && paidNum > 0 && dueNum > 0 && (
                    <tr><th>{td('invoices.printMeta.balanceDue')}:</th><td>{mTotal(dueNum)}</td></tr>
                  )}
                </tbody>
              </table>

              {/* The grand total spelled out cheque-style — the line a
                  hand-written invoice always carries, and the one figure on the
                  sheet nobody can mis-read a digit of. In the PAPER's own
                  language; both lines only on the bilingual sheet, where every
                  label already prints twice. A5 carries it too (compact, no
                  box) by request: it is the line the shop's hand-written
                  invoices always had. */}
              {(
                <div className={plain ? 'words a5words' : 'words'}>
                  {(bilingual ? (docAr ? ['ar', 'en'] as const : ['en', 'ar'] as const) : [docLocale] as const).map((lng) => (
                    <div className="wline" key={lng} dir={lng === 'ar' ? 'rtl' : 'ltr'}>
                      <b>{fixedT(lng)('invoices.printMeta.amountInWords', lng === 'ar' ? 'المبلغ كتابةً' : 'Amount in words')}: </b>
                      <span>{amountInWords(cv(totalNum), paperCcy, lng)}</span>
                      {/* Paid in another currency: what the customer really handed over, in words too. */}
                      {cash && rows.paid && cash.currency !== paperCcy && (
                        <span>
                          {lng === 'ar' ? '، ' : ', '}
                          {fixedT(lng)('invoices.printMeta.equivalent', lng === 'ar' ? 'أي ما يعادل' : 'i.e. the equivalent of')}{' '}
                          {amountInWords(cash.paid, cash.currency, lng)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {fxNote && <div className="fxnote" dir={docDir}>{fxNote}</div>}

              {showNotes && invoice.notes && (
                <div className="clsc-notes">
                  <b>{td('invoices.printMeta.notes')}</b>
                  {/* plaintext: an Arabic note on an English sheet keeps its own
                      word order, the same rule the product names follow. */}
                  <span>{invoice.notes}</span>
                </div>
              )}

              {/* The operator's own line on this one sheet — the printed
                  equivalent of writing on the paper by hand, and it is only
                  ever that: nothing here is sent to the server or kept, so the
                  next print of the same invoice starts blank. It carries no
                  "Notes" label precisely so it cannot be mistaken for the
                  document's own notes printed just above it. */}
              {/* Only while it differs from what the document already prints (or the
                  document's own notes are switched off): once saved, the notes block
                  above carries it, and a second copy would print it twice. */}
              {printNote.trim() && (noteDirty || !showNotes) && (
                <div className="clsc-notes print-note">{printNote}</div>
              )}
            </div>

            {/* Foot: a rule and the sales-department mark, nothing else — the
                signature line, barcode, QR and terms the sheet used to carry
                are all absent from the shop's own paper. The share link still
                reaches customers through the WhatsApp button beside Print. */}
            <div className="inv-foot">
              <hr className="chr" />
              {pro ? (
                /* Pro foot: the share QR (when this document has a public link
                   to encode — supplier papers never do) and two signature
                   lines. The classic sheet keeps its bare foot below, matching
                   the shop's old paper. */
                <div className="pro-foot">
                  {publicLink && (
                    <div className="pro-qr">
                      <div
                        className="box"
                        role="img"
                        aria-label={qrCaption}
                        title={publicLink.url}
                        dangerouslySetInnerHTML={{ __html: publicLink.qrSvg }}
                      />
                      <div className="cap">{qrCaption}</div>
                    </div>
                  )}
                  {/* A framed box per signature — the name on top, room to sign
                      under it, a ruled line to sign on. */}
                  <div className="pro-sign">
                    <div className="sig"><span className="cap">{bi('invoices.printMeta.receiverSign', "Receiver's signature")}</span><span className="line" /></div>
                    <div className="sig"><span className="cap">{bi('invoices.printMeta.signature', 'Authorized Signature')}</span><span className="line" /></div>
                  </div>
                </div>
              ) : (
                <div className="clsc-dept">{td('invoices.printMeta.salesDept', 'Sales Department')}</div>
              )}
              {/* Pro: the closing line every professional invoice ends on, and
                  the shop's contact strip under it. */}
              {pro && (
                <div className="pro-bottom">
                  <div className="ty">{bi('invoices.printMeta.thankYou', 'Thank you for your business')}</div>
                  {(sellerPhone || sellerEmail || sellerWeb || sellerAddr) && (
                    <div className="ct">
                      {/* Each item keeps the SHEET's direction (the dot between them
                          sits on its start side); a left-to-right value is isolated
                          inside it, or an English item's dot lands on its far side
                          and two items run together. */}
                      <span className="nm">{sellerName}</span>
                      {sellerPhone && <span><Tel>{sellerPhone}</Tel></span>}
                      {sellerEmail && <span><bdi dir="ltr">{sellerEmail}</bdi></span>}
                      {sellerWeb && <span><bdi dir="ltr">{sellerWeb}</bdi></span>}
                      {sellerAddr && <span><bdi>{sellerAddr}</bdi></span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </div>
        ) : (
          /* 80mm thermal receipt. Follows the language switch, but never prints
             bilingually: 302px of paper cannot carry two labels per row without
             every line wrapping. */
          <div className={`roll${docAr ? ' ar' : ''}${bold ? ' bold' : ''}`} dir={docDir}>
            <div className="rc">
              {sellerLogo && <img className="rlogo" src={sellerLogo} alt={sellerName} onError={() => setLogoFailed(true)} />}
              <div className="rbrand">{sellerName}</div>
              {sellerSub && <div className="rsub">{sellerSub}</div>}
              {sellerPhone && <div className="rsub"><Tel>{sellerPhone}</Tel></div>}
              <div className="rvat">{docTitle} · {invoice.docNumber}</div>
            </div>
            <div className="rdiv" />
            <div className="rmeta"><span>{td('invoices.printMeta.date')}</span><span>{formatDate(invoice.docDate, { locale: docLocale })}</span></div>
            {invoice.supplierInvoiceNo && (
              <div className="rmeta">
                <span>{td('invoices.printMeta.supplierInvoiceNo', 'Supplier invoice no.')}</span>
                <span dir="ltr">{invoice.supplierInvoiceNo}</span>
              </div>
            )}
            {party && <div className="rmeta"><span>{td(cfg.partyLabelKey)}</span><span>{party.displayName}</span></div>}
            <div className="rdiv" />
            {productLines.map((l) => (
              <div className="ritem" key={l.lineNo}>
                <div className="top">
                  {/* Same unit fallback as the A4 column, so the receipt and the
                      sheet name the same unit for the same line. */}
                  <span>{lineName(l)}{(l.unitName ?? l.itemUom) ? ` · ${unitLabel(l.unitName ?? l.itemUom, docLocale)}` : ''}</span>
                  <span>{m(l.lineTotal)}</span>
                </div>
                <div className="sub"><span>{n(Number(l.quantity))} × {m(l.unitPrice)}</span></div>
                {lineCode(l) && <div className="rlncode">{lineCode(l)}</div>}
              </div>
            ))}
            <div className="rdiv" />
            <div className="rtot"><span>{td('invoices.printMeta.subtotal')}</span><span className="v">{m(productsSubtotal)}</span></div>
            {chargeLines.map((l) => (
              <div className="rtot" key={l.lineNo}><span>{l.description ?? td('invoices.printMeta.delivery', 'Delivery')}</span><span className="v">{m(l.lineTotal)}</span></div>
            ))}
            {invoice.discountTotal !== undefined && Number(invoice.discountTotal) > 0 && (
              <div className="rtot"><span>{td('invoices.printMeta.discount', 'Discount')}{invoice.couponCode ? ` (${invoice.couponCode})` : ''}</span><span className="v">−{m(invoice.discountTotal)}</span></div>
            )}
            {hasTax && <div className="rtot"><span>{td('invoices.printMeta.tax')}</span><span className="v">{m(invoice.taxTotal)}</span></div>}
            <div className="rtot grand"><span>{td('invoices.printMeta.total')}</span><span>{m(invoice.total)}</span></div>
            {cash && (
              <>
                <div className="rtot cash">
                  <span>{td(`invoices.printMeta.${paidLabelKey(cash.method)}`, PAID_LABEL_DEFAULT[paidLabelKey(cash.method)])}</span>
                  <span className="v" dir="ltr">{cashAmt(cash.paid, cash.currency)}</span>
                </div>
                {cash.equivalent !== null && (
                  <div className="rtot cash-sub">
                    <span>{td('invoices.printMeta.equivalent', 'i.e. the equivalent of')}</span>
                    <span className="v" dir="ltr">{cashAmt(cash.equivalent, invoice.currencyCode)}</span>
                  </div>
                )}
                {cash.handed !== null && (
                  <div className="rtot cash-sub">
                    <span>{td('invoices.printMeta.cashHanded', 'Cash handed over')}</span>
                    <span className="v" dir="ltr">{cashAmt(cash.handed, cash.currency)}</span>
                  </div>
                )}
                {cash.change && (
                  <div className="rtot cash-sub">
                    <span>{td('invoices.printMeta.change', 'Change')}</span>
                    <span className="v" dir="ltr">{cashAmt(cash.change.amount, cash.change.currency)}</span>
                  </div>
                )}
              </>
            )}
            <div className="rwords">
              {amountInWords(cv(totalNum), paperCcy, docLocale)}
              {cash && cash.currency !== paperCcy && (
                <>
                  {docAr ? '، ' : ', '}
                  {td('invoices.printMeta.equivalent', 'i.e. the equivalent of')}{' '}
                  {amountInWords(cash.paid, cash.currency, docLocale)}
                </>
              )}
            </div>
            {fxNote && <div className="rwords fxnote">{fxNote}</div>}
            {/* Same print-only annotation the sheet carries — the receipt is
                printed from the same toolbar, so it obeys the same note. */}
            {printNote.trim() && (noteDirty || !showNotes) && <div className="rnote">{printNote}</div>}
            <div className="rthanks">{td('invoices.printMeta.thanks', 'Thank you!')}</div>
            {barcodeRoll && (
              /* Where a receipt's barcode belongs: at the foot, so it can be
                 scanned to pull the sale up for a return or a reprint. */
              <div
                className="rbc"
                role="img"
                aria-label={invoice.docNumber}
                dangerouslySetInnerHTML={{ __html: barcodeRoll }}
              />
            )}
            {publicLink && (
              <div className="rqr">
                {/* Server-rendered QR markup from our own authed API. */}
                <div
                  className="box"
                  role="img"
                  aria-label={qrCaption}
                  title={publicLink.url}
                  dangerouslySetInnerHTML={{ __html: publicLink.qrSvg }}
                />
                <div className="cap">{qrCaption}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* Scoped paper styles. The paper itself is always white with dark text (a real
   printable document); only the brand band/accents use --paper-brand/-accent. */
const CSS = `
.docview { min-height: 100vh; display: flex; flex-direction: column; background: var(--color-bg-subtle); color: var(--color-fg);
  /* Use the app's system fonts: Latin first, with the Arabic system font (Cairo)
     as the fallback so Arabic text (names, descriptions) renders in the same font
     as the rest of the app instead of the browser's default Arabic face. */
  font-family: var(--font-sans), var(--font-sans-arabic), system-ui, sans-serif; }
.docview[dir="rtl"] { font-family: var(--font-sans-arabic), system-ui, sans-serif; }
/* The receipt picks its font from the DOCUMENT's language, not the app's — an
   English receipt printed out of an Arabic dashboard would otherwise inherit
   the Arabic face for its Latin text. */
.roll { font-family: var(--font-sans), var(--font-sans-arabic), system-ui, sans-serif; }
.roll.ar { font-family: var(--font-sans-arabic), var(--font-sans), system-ui, sans-serif; }
/* The SHEET wears the old shop system's face verbatim. That system declared
   'Droid Arabic Kufi' from a long-dead fonts.gstatic.com/ea URL, so what its
   printouts actually render — and what this sheet must match — is the browser's
   serif fallback. Same two names here, same fallback, same paper. */
.page, .page.ar { font-family: 'Droid Arabic Kufi', serif; }
/* Alternative faces for the sheet. Held as classes, not as strings in the
   component: a font stack belongs in the stylesheet, and the default one above
   is the old system's own — a shop that never touches this keeps the replica. */
.page.f-sans, .page.f-sans.ar { font-family: var(--font-sans-arabic), var(--font-sans), system-ui, sans-serif; }
.page.f-system, .page.f-system.ar { font-family: system-ui, 'Segoe UI', Tahoma, sans-serif; }
.page.f-serif, .page.f-serif.ar { font-family: 'Times New Roman', Times, serif; }
/* The second language under a label in bilingual mode. Inherits the heading's
   case and colour so it reads as one label in two languages rather than as a
   caption; only the size, weight and letter-spacing step down. */
.alt { display: block; font-size: .82em; font-weight: 600; opacity: .72; letter-spacing: 0; line-height: 1.3; }
/* Sticky, so its height is stolen from the paper for the whole scroll — kept low
   deliberately. It wraps on a narrow window, which is why min-height is a floor
   and not a fixed height. */
.dv-bar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  min-height: 44px; padding: 5px 16px; border-bottom: 1px solid var(--color-border); background: var(--color-bg); }

.dv-back { display: inline-flex; align-items: center; gap: 7px; height: 30px; padding: 0 10px; border-radius: 8px;
  color: var(--color-fg-muted); font-size: 13px; font-weight: 600; border: 1px solid var(--color-border); background: var(--color-bg-subtle); }
.dv-back:hover { color: var(--color-fg); }
[dir="rtl"] .dv-back svg { transform: scaleX(-1); }
.dv-title { font-weight: 800; font-size: 14px; }
.dv-spacer { flex: 1; }
/* In the top bar the foot is just the two buttons where they always were. */
.dv-foot { display: contents; }
.dv-color { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 10px; background: var(--color-bg-subtle); border: 1px solid var(--color-border); border-radius: 8px; cursor: pointer; }
.dv-color .dv-swatch { width: 15px; height: 15px; flex: none; border-radius: 4px; box-shadow: inset 0 0 0 1px rgba(0,0,0,.15); }
.dv-color select { border: 0; background: transparent; color: var(--color-fg); font-size: 12px; font-weight: 700; cursor: pointer; outline: none; max-width: 120px; }
.dv-check { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600;
  color: var(--color-fg-muted); cursor: pointer; user-select: none; }
.dv-check input { accent-color: var(--color-brand); cursor: pointer; }
/* Sheet options: one disclosure button in the bar, and a panel that takes a full
   row of the (wrapping) bar when it is open — the eight column switches need the
   width, and stealing it from the buttons beside them would push Print off the
   end on a laptop. */
.dv-opt-btn { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 12px; border-radius: 8px;
  border: 1px solid var(--color-border); background: var(--color-bg-subtle); color: var(--color-fg-muted);
  font-size: 12px; font-weight: 700; cursor: pointer; }
.dv-opt-btn:hover { color: var(--color-fg); }
.dv-opt-btn[aria-expanded="true"] { color: var(--color-fg); border-color: var(--color-brand); }
.dv-opts { flex: 0 0 100%; display: flex; flex-wrap: wrap; align-items: stretch; justify-content: flex-start; gap: 14px 0;
  padding: 9px 2px 5px; border-top: 1px solid var(--color-border); }
/* Each group is its own COLUMN: its label on top and its switches stacked
   under it, packed from the start edge. As one wrapped row the panel read as a
   run-on sentence of unrelated switches, and the per-column width boxes sat at
   a different offset on every line; stacked, they line up in a strip the eye
   can run down. */
.dv-opts .grp { display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
  padding-inline-end: 14px; }
.dv-opts .grp + .grp { border-inline-start: 1px solid var(--color-border); padding-inline-start: 14px; }
.dv-opts .grp .dv-check { width: auto; }
.dv-opts .grp.note { flex: 1 1 260px; flex-direction: column; align-items: stretch; gap: 4px; }
.dv-opts .lbl { font-size: 11px; font-weight: 800; letter-spacing: .04em; color: var(--color-fg-muted); }
.dv-opts textarea { width: 100%; min-height: 44px; resize: vertical; padding: 6px 8px; font: inherit; font-size: 12px;
  border: 1px solid var(--color-border); border-radius: 6px; background: var(--color-bg); color: var(--color-fg); }
.dv-opts .hint { font-size: 11px; line-height: 1.4; color: var(--color-fg-muted); }
/* Swatches. The ring rather than a tick: these are pale washes and a mark drawn
   on top of one is harder to see than a border drawn around it. */
.docview { --tint-1: #E1ECFF; --tint-2: #EDEFF2; --tint-3: #E4F2E8; --tint-4: #FBF0DA; --tint-5: #F6E3E6; }
.dv-w { width: 46px; height: 22px; padding: 0 4px; border-radius: 4px; border: 1px solid var(--color-border);
  background: var(--color-bg); color: var(--color-fg); font-size: 11px; font-weight: 700; text-align: center; }
/* Label then control, sitting together — not a name at one edge and its value
   at the other. */
.dv-row { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600;
  color: var(--color-fg); }
.dv-sel { height: 28px; padding: 0 6px; border-radius: 6px; border: 1px solid var(--color-border);
  background: var(--color-bg); color: var(--color-fg); font-size: 12px; font-weight: 700; cursor: pointer; }
/* Level sliders (compactness, text size): the track shows the progress, the
   label beside it names the stop — a bare thumb on three unmarked notches
   would say nothing. */
.dv-slide input[type="range"] { width: 90px; accent-color: var(--color-brand); cursor: pointer; }
.dv-slide .dv-slide-val { min-width: 44px; font-size: 11px; font-weight: 700; color: var(--color-fg-muted); }
.dv-swatch { width: 22px; height: 22px; border-radius: 5px; border: 1px solid var(--color-border);
  cursor: pointer; padding: 0; }
.dv-swatch.on { outline: 2px solid var(--color-brand); outline-offset: 1px; }
.dv-seg { display: inline-flex; padding: 3px; gap: 2px; background: var(--color-bg-subtle); border: 1px solid var(--color-border); border-radius: 8px; }
.dv-seg button { border: 0; background: transparent; color: var(--color-fg-muted); height: 28px; padding: 0 12px; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; }
.dv-seg button[aria-pressed="true"] { background: var(--color-brand); color: var(--color-brand-contrast); }
/* Pre-printed-paper margins, in mm. Only shown in plain mode. */
.dv-mm { display: inline-flex; align-items: center; gap: 8px; height: 36px; padding: 0 10px;
  background: var(--color-bg-subtle); border: 1px solid var(--color-border); border-radius: 8px;
  font-size: 11px; font-weight: 700; color: var(--color-fg-muted); }
.dv-mm label { display: inline-flex; align-items: center; gap: 4px; }
.dv-mm input { width: 44px; height: 24px; padding: 0 4px; border: 1px solid var(--color-border); border-radius: 5px;
  background: var(--color-bg); color: var(--color-fg); font-size: 11px; font-weight: 700; text-align: center; }
/* Two classes deep so it beats the Button component's own h-8 utility. */
.docview .dv-wa { height: 36px; border-radius: 8px; }
.dv-print { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 14px; border-radius: 8px;
  border: 0; background: var(--color-brand); color: var(--color-brand-contrast); font-size: 13px; font-weight: 700; cursor: pointer; }
/* The public copy's own share link. WhatsApp green rather than the brand, so it
   reads as "send this elsewhere" next to the brand-coloured Print. */
.dv-wa-share { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 14px; border-radius: 8px;
  border: 0; background: #25D366; color: #06241a; font-size: 13px; font-weight: 700; cursor: pointer;
  text-decoration: none; white-space: nowrap; }
/* Get-the-app: outlined, so the row reads action (Print, brand) → share (green)
   → invitation (quiet). Tokens, not hex — it must hold up on any tenant theme. */
.dv-app { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 14px; border-radius: 8px;
  border: 1px solid var(--color-border-strong); background: var(--color-bg); color: var(--color-fg); font-size: 13px;
  font-weight: 700; cursor: pointer; text-decoration: none; white-space: nowrap; }
.dv-stage { flex: 1; overflow: auto; padding: 32px 20px 64px; display: flex; flex-direction: column; align-items: center; gap: 24px; }
.dv-empty { font-size: 14px; color: var(--color-fg-muted); text-align: center; padding: 48px 0; }

/* Fit-to-width wrapper. Capped at one A4 width, so on anything narrower the
   ratio measured in JS is < 1 and the sheet is SCALED rather than squeezed —
   a squeezed 794px page makes the items table unreadable on the phone most
   customers open the WhatsApp link with. --paper-fit-h is the scaled height
   (transforms don't affect layout, so without it the wrapper would still
   reserve 1123px and leave dead space under the paper). */
.paper-fit { width: 100%; max-width: var(--paper-w, 794px); height: var(--paper-fit-h, auto); }
.paper-fit > .page { max-width: none; transform: scale(var(--paper-fit, 1)); transform-origin: top left; }

/* The classic sheet's palette IS the old theme's: title/bold ink #111111, body
   #6E6E6E, hairlines #E3E3E3, table grid #f3f3f3, table head #E1ECFF. Sizes are
   the old 1200px design × 0.7 — the scale the browser applied when it printed
   that page onto A4. */
.page { width: 794px; max-width: 100%; min-height: 1123px; background: #fff; color: #6E6E6E;
  font-size: calc(11px * var(--type, 1)); line-height: 1.5;
  box-shadow: 0 30px 80px -20px rgba(0,0,0,.45), 0 8px 24px -8px rgba(0,0,0,.3); border-radius: 3px;
  position: relative; overflow: hidden; }
.page b, .page th { color: #111; }
/* "Bold text". The body moves a whole step, and the pieces that already had a
   weight of their own move with it — a 700 body under 700 labels reads flat, and
   the point of the option is a sheet that survives a failing printer, not one
   that loses its hierarchy. Anything already at 800 stays there. */
.page.bold, .roll.bold { font-weight: 700; }
.page.bold .clsc-cust div, .page.bold .items thead th, .page.bold .ctot th,
.roll.bold .ritem .top, .roll.bold .rtot.grand { font-weight: 800; }

.inv-top { padding: 24px 35px 0; }
.clsc-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.clsc-logo .logo { width: 92px; max-height: 140px; object-fit: contain; display: block; }
.clsc-logo .mk { width: 64px; height: 64px; border-radius: 12px; background: #f3f3f3; display: grid;
  place-items: center; font-weight: 700; font-size: 20px; color: #111; }
.clsc-title { text-align: end; }
.clsc-title .dt { font-size: calc(28px * var(--type, 1)); font-weight: 700; color: #111; margin: 0 0 4px; line-height: 1.3; }
/* Direct children only: the supplier's number is a span INSIDE its line, and a
   bare descendant selector made it a block of its own — the value dropped under
   its label. */
.clsc-title > span { display: block; font-size: calc(11px * var(--type, 1)); }
hr.chr { border: 0; border-top: 1px solid #E3E3E3; margin: 12px 0; }
.inv-top hr.chr1 { margin: 17px 0 0; }

.inv-body { padding: 0 35px; }
.clsc-info { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin: 17px 0; }
/* The old sheet's leading, verbatim: its body ran on a FIXED 22px line over a
   16px base (1.375) and the issuer paragraph on 25.136px (1.571). Both blocks
   used to sit on 1.7/1.9 here, which stood the recipient block a third of a
   line taller per row and pushed the items table down the page. */
.clsc-cust div { font-weight: 700; line-height: 1.375; }
/* 18px x 0.7. The issuer paragraph keeps its bottom margin because it is the
   taller of the two columns and that margin is part of the gap the old paper
   leaves between the recipient block and the table. */
.clsc-meta p { margin: 0 0 12px; line-height: 1.571; }

/* The old table verbatim: fixed layout (3% index · 18% name · 10% pack · 10%
   qty, the rest sharing what remains), everything centred, a #f3f3f3 grid, the
   #E1ECFF head, and the qty column washed #cccccc33. */
table.items { width: 100%; border-collapse: collapse; table-layout: fixed; border: 1px solid #f3f3f3; margin: 0 0 18px; }
.items th, .items td { border-inline-end: 1px solid #f3f3f3; padding: 3px 2px; font-size: calc(10px * var(--type, 1));
  text-align: center; overflow-wrap: break-word; }
.items thead th { background: var(--head-tint, #E1ECFF); font-weight: 700; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.items tbody tr { border-top: 1px solid #f3f3f3; }
.items th.c-idx { width: 3%; }
/* A two-digit row number stays on one line — at 3% it broke as "6" over "9". */
.items th.c-idx, .items td.idx { white-space: nowrap; }
/* The product name carries the only unbounded string on the sheet and it is
   what someone actually reads down the column; the pack and quantity carry "1"
   and were given the same 10% as columns holding money. Widths are percentages
   under table-layout:fixed, so a hidden column simply returns its share to the
   rest — these need not add to 100. */
.items th.l { width: 30%; }
.items th.c-pack { width: 7%; }
.items th.c-qty { width: 6%; }
.items td.c-qty { background: #cccccc33; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.items td.c-qty, .items td.c-price, .items td.c-total { white-space: nowrap; font-variant-numeric: tabular-nums; }
/* The picture gets a column of its own, so every row's image lines up down the page
   instead of shifting with the length of the name beside it.
   The old paper printed the WHOLE picture — 80px wide (x0.7 = 56), its own
   natural height, 6px (x0.7 = 4) of white inside a 12px (x0.7 = 8) radius, and
   no crop. A square box with object-fit:cover sliced the top and bottom off
   every portrait photo, which on a stationery catalogue is where the product
   is. Height is free, exactly as it was there, so a tall photo makes a tall
   row rather than a cropped one. */
.items .thumb { display: block; width: 56px; margin: 0 auto; }
.items .thumb img { display: block; box-sizing: border-box; width: 56px; height: auto;
  padding: 4px; border-radius: 8px; background: #fff; }
.items tbody td .subdesc { display: block; font-size: calc(9px * var(--type, 1)); color: #8b8b8b; }
/* First-strong base direction per name: an Arabic product name on an English
   paper (or vice versa) otherwise renders its words back to front — the same
   fix the label designer needed. */
.items td .desc, .items td .subdesc { unicode-bidi: plaintext; }
/* Codes are LTR whatever the document's language — a code read right-to-left is
   a different code. Same face as everything else, exactly like the old paper. */
.items td.c-code { direction: ltr; unicode-bidi: isolate; white-space: nowrap; }

/* The classic totals list: bold start-side labels, values just beside them,
   nothing filled or boxed. */
table.ctot { width: auto; border-collapse: collapse; margin: 0; border: 0; }
/* Same 22/16 leading as the rest of the old sheet — the sheet's own 1.5 stood
   this list four rows tall where the old one was three and a half. */
.ctot th, .ctot td { border: 0; padding: 2px 14px; font-size: calc(11px * var(--type, 1)); line-height: 1.375; }
.ctot tr.cash th, .ctot tr.cash td { font-weight: 800; }
.ctot tr.cash-sub th, .ctot tr.cash-sub td { font-weight: 400; font-size: calc(10px * var(--type, 1)); color: #444; padding-top: 0; }
.ctot th { text-align: start; font-weight: 700; }
.ctot td { text-align: end; font-variant-numeric: tabular-nums; }
/* A note is a remark ABOUT the document, so it reads as one: set off in its own
   tinted block with a rule on the start edge, rather than a bare line of text
   that the eye takes for one more total. unicode-bidi:plaintext so an Arabic
   note on an English sheet keeps its own word order. */
.clsc-notes { margin: 12px 0 0; padding: 7px 10px; background: #F7F9FF;
  border-inline-start: 3px solid #E1ECFF; border-radius: 3px;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.clsc-notes b { display: block; margin-bottom: 2px; font-size: 9.5px; letter-spacing: .03em;
  text-transform: uppercase; color: #545B72; }
.clsc-notes span { display: block; white-space: pre-wrap; unicode-bidi: plaintext; }
/* The typed-for-this-print note. plaintext for the same reason the item names
   need it: an Arabic note on an English sheet (or the reverse) otherwise prints
   its words back to front. Line breaks are kept — the operator typed them. */
.page .print-note { white-space: pre-wrap; unicode-bidi: plaintext; }

.inv-foot { padding: 0 35px 24px; }
.clsc-dept { font-weight: 700; color: #111; font-size: 14px; }
/* The API sizes the SVG for its own defaults; the CSS box is the real size. */
.roll .rqr .box svg { display: block; width: 100%; height: 100%; shape-rendering: crispEdges; }

/* 80mm thermal receipt */
.roll { width: 302px; max-width: 100%; background: #fff; color: #1a1a1a; padding: 20px 18px 26px;
  box-shadow: 0 24px 60px -16px rgba(0,0,0,.42); font-variant-numeric: tabular-nums; }
.roll .rc { text-align: center; }
.roll .rlogo { width: 56px; height: 56px; object-fit: contain; margin: 0 auto 6px; display: block; }
.roll .rbrand { font-size: 18px; font-weight: 800; letter-spacing: .5px; }
.roll .rsub { font-size: 10.5px; color: #555; margin-top: 2px; }
.roll .rvat { font-size: 10px; color: #555; margin-top: 4px; }
/* Foot-of-receipt barcode. Centred, LTR, and never scaled below its natural
   size — shrinking a thermal barcode is what makes it stop scanning. */
.roll .rbc { margin: 8px auto 2px; text-align: center; line-height: 0; direction: ltr; }
.roll .rbc svg { display: inline-block; max-width: 100%; }
.roll .rdiv { border-top: 1.5px dashed #bbb; margin: 12px 0; }
.roll .rmeta { display: flex; justify-content: space-between; font-size: 10.5px; color: #444; padding: 2px 0; gap: 10px; }
.roll .ritem { padding: 5px 0; }
.roll .ritem .top { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; gap: 8px; }
.roll .ritem .sub { display: flex; justify-content: space-between; font-size: 10px; color: #666; margin-top: 1px; }
.roll .ritem .rlncode { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 9.5px;
  color: #888; letter-spacing: .04em; direction: ltr; unicode-bidi: isolate; margin-top: 1px; }
.roll .rtot { display: flex; justify-content: space-between; font-size: 11.5px; padding: 3px 0; gap: 8px; }
.roll .rtot.grand { font-size: 16px; font-weight: 800; padding: 6px 0; }
.roll .rtot.cash { font-weight: 800; font-size: 12.5px; border-top: 1px dashed #000; padding-top: 5px; }
.roll .rtot.cash-sub { font-size: 10.5px; padding: 1px 0; }
.fxnote { margin-top: 6px; padding: 4px 6px; border: 1px dashed #999; border-radius: 4px; font-size: 10px; line-height: 1.45; color: #333; }
.roll .rwords { margin-top: 8px; padding-top: 8px; border-top: 1.5px dashed #bbb; font-size: 10.5px; font-weight: 600; line-height: 1.5; color: #333; text-align: center; }
.roll .rnote { margin-top: 8px; font-size: 10.5px; line-height: 1.5; text-align: center;
  white-space: pre-wrap; unicode-bidi: plaintext; }
.roll .rthanks { text-align: center; font-size: 11px; margin-top: 12px; font-weight: 700; }
.roll .rqr { display: flex; flex-direction: column; align-items: center; margin-top: 12px; }
/* Thermal heads resolve ~0.125mm dots, so give the code room: ~29mm of white. */
.roll .rqr .box { width: 140px; height: 140px; background: #fff; padding: 4px;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.roll .rqr .cap { font-size: 9.5px; color: #555; margin-top: 4px; text-align: center; }

/* ── "Data only": print onto the shop's OWN pre-printed A5 paper ────────────
   The print shop already put the letterhead at the top and the footer at the
   bottom of the sheet; we print only what sits between them. Everything the
   paper already carries is switched OFF here rather than duplicated into a
   second component — same document, pieces suppressed. */
/* A5 portrait = 148x210mm ≈ 559x794px at 96dpi (the transpose of the A4 sheet
   above; JournalVoucherPrintPage pins the same 559px≈148mm). Fonts are NOT
   scaled: 96px/in maps these pixels 1:1 to millimetres, so 12px prints the same
   physical size on A5 as on A4 — only the WIDTH has to give. */
.page.plain { width: 559px; min-height: 794px; padding: var(--plain-top) var(--plain-side) var(--plain-bottom);
  /* flex + overflow:hidden are what let the A4 sheet pin its footer; both
     mis-fragment a multi-page print, and half-height paper spills onto page 2
     constantly. */
  display: block; overflow: visible; }
.plain .inv-top,        /* classic letterhead — that is their pre-printed paper */
/* The foot (rule + sales-department mark) goes too: this sheet is handed over
   on paper that already carries the shop's own footer, and every millimetre it
   takes is a millimetre off an already short page (a 40mm letterhead still
   leaves only ~140mm of body on A5). */
.plain .inv-foot,
/* Screen-only guide. The letterhead band above still earns its keep — it is the
   big one being reserved — but the footer's just repeats what the blank space
   already shows. */
.plain .plain-reserve.bottom { display: none; }
.plain-reserve { position: absolute; inset-inline: 0; display: grid; place-items: center; margin: 0 6px; overflow: hidden;
  border: 1px dashed #C6CDDC; border-radius: 6px; color: #A6ACBD; font-size: 10px; }
/* max() because a 1-2mm band would compute a NEGATIVE height, which is invalid
   and silently falls back to auto — an auto-height bar sits on top of the data. */
.plain-reserve.top { top: 6px; height: max(0px, calc(var(--plain-top) - 12px)); }
.plain-reserve.bottom { bottom: 6px; height: max(0px, calc(var(--plain-bottom) - 12px)); }
.plain .inv-body { padding: 0; }
/* The gap above the table on A5 was two things, not one: this block's own
   bottom margin, and the 12px the meta paragraph carries INSIDE it, which pads
   the block from within and reads as more space than the margin admits to.
   Both come down — on a 140mm body that gap is a line of the invoice. */
.plain .clsc-info { margin: 8px 0 3px; }
.plain .clsc-meta p { margin-bottom: 0; }
.plain .items thead th { background: transparent; color: #1a2230; border-bottom: 1.5px solid #1a2230; padding: 6px 4px; }
/* …unless the sheet is asked to look like its A4 self. A table heading is not
   pre-printed on anybody's stationery, so tinting it costs a little toner and
   buys an A5 that reads like the same document as the A4. */
.plain.head-tint .items thead th { background: var(--head-tint, #E1ECFF); border-bottom: 1px solid #E3E3E3;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* A5 asked for its letterhead back: the classic head returns, with the number. */
.plain.a5head .inv-top { display: block; }
.plain.a5foot .inv-foot { display: block; }
/* Tight rows. The A5 body is about 140mm and this is the only knob that decides
   how many lines reach the page rather than the next one. */
.page.dense .items thead th, .page.dense .items tbody td { padding-top: 3px; padding-bottom: 3px; }
.page.dense .clsc-info, .page.dense .clsc-notes { margin-top: 8px; }
.plain .items tbody td { padding: 6px 4px; }
.plain td.c-qty { background: transparent; }   /* the wash is ink, not data */
.plain .thumb, .plain th.c-img, .plain td.c-img { display: none; }   /* photos are ink, not data */

/* ── The spelled-out total (both A4 faces) ──────────────────────────────────
   One line per language, each in its own base direction — an Arabic sentence
   inside an English sheet keeps its own word order and vice versa. */
.words { margin: 14px 0 0; padding: 8px 12px; border: 1px dashed #cdd4e2; border-radius: 8px; }
.words .wline { font-size: calc(10.5px * var(--type, 1)); line-height: 1.8; unicode-bidi: plaintext; text-align: start; }
.words .wline b { font-weight: 700; }
/* A5: the spelled-out total as a plain ruled line, no box (nothing a short page
   cannot afford), and it follows the compact levels like the rest of the sheet. */
.plain .words.a5words { margin: 6px 0 0; padding: 4px 0 0; border: 0; border-top: 1px solid #1a2230; border-radius: 0; }
.plain .words.a5words .wline { font-size: calc(10px * var(--type, 1)); line-height: 1.6; }
.plain.dense .words.a5words { margin-top: 4px; padding-top: 3px; }
.plain.dense2 .words.a5words .wline { font-size: calc(9px * var(--type, 1)); line-height: 1.45; }

/* ── A42: the professional face ─────────────────────────────────────────────
   The SAME tree as the classic A4 — pro is a coat of paint plus three extra
   blocks (the meta strip, the spelled-out total's card, the QR/signature
   foot), so the two A4s can never disagree about a figure. Palette: one dark
   navy ink (#16233b) against pale blue-greys, everything else white — a suit,
   not a poster, and cheap to print. */
.page.pro { color: #3a4252; font-family: var(--font-sans-arabic), var(--font-sans), system-ui, sans-serif; }
.page.pro::before { content: ''; position: absolute; top: 0; inset-inline: 0; height: 6px;
  background: #16233b; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.page.pro b, .page.pro th { color: #16233b; }
.page.pro .clsc-title .dt { font-size: calc(21px * var(--type, 1)); font-weight: 800; letter-spacing: .08em;
  text-transform: uppercase; color: #16233b; }
/* The number/date lines under the title step aside — the meta strip states them. */
.page.pro .clsc-title > span { display: none; }
.page.pro .inv-top hr.chr1 { display: none; }
.pro-meta { display: flex; margin-top: 14px; border: 1px solid #e3e6ee; border-radius: 8px; overflow: hidden; }
.pro-meta .cell { flex: 1 1 0; display: flex; flex-direction: column; gap: 1px; padding: 7px 12px; min-width: 0; }
.pro-meta .cell + .cell { border-inline-start: 1px solid #e3e6ee; }
.pro-meta .k { font-size: calc(8.5px * var(--type, 1)); font-weight: 700; letter-spacing: .06em;
  text-transform: uppercase; color: #8a92a6; }
.pro-meta .k .alt { font-size: 1em; }
.pro-meta .v { font-size: calc(11.5px * var(--type, 1)); font-weight: 800; color: #16233b;
  font-variant-numeric: tabular-nums; overflow-wrap: break-word; text-align: start; }
/* The recipient reads as a card; the issuer stays a quiet paragraph beside it. */
.page.pro .clsc-info { margin: 16px 0; }
.page.pro .clsc-cust { background: #f6f8fc; border-radius: 8px; padding: 10px 14px;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* Dark head, no vertical grid, zebra rows — the pro table. */
.page.pro table.items { border: 0; }
.page.pro .items th, .page.pro .items td { border-inline-end: 0; padding: 6px 6px; }
.page.pro .items thead th { background: #16233b; color: #fff;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.page.pro .items thead th .alt { opacity: .78; }
.page.pro .items tbody tr { border-top: 1px solid #edf0f6; }
.page.pro .items tbody tr:nth-child(even) td { background: #f8fafd;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.page.pro .items td.c-qty { background: transparent; }
/* The body/foot hairlines belong to the classic sheet; pro rules itself off. */
.page.pro hr.chr { display: none; }
/* Totals sit at the end side, and the grand total is the loudest thing on the
   sheet — filled navy, white figures. */
.page.pro table.ctot { margin-inline-start: auto; min-width: 300px; margin-top: 14px; }
.page.pro .ctot th, .page.pro .ctot td { padding: 4px 14px; }
.page.pro .ctot tr.g th, .page.pro .ctot tr.g td { background: #16233b; color: #fff; font-weight: 800;
  font-size: calc(12.5px * var(--type, 1)); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.page.pro .words { border: 1px solid #e3e6ee; background: #fbfcff; border-radius: 8px;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* The sheet is a column whose foot sits at the BOTTOM of the page — a short
   invoice no longer ends with its signatures halfway down and a blank half
   sheet under them. The body takes the slack; a long invoice simply runs on and
   the foot follows its last line. (Print gets a page-tall min-height from the
   page's own @page margin — see the print <style>.) */
.page.pro { display: flex; flex-direction: column; }
.page.pro .inv-body { flex: 1 0 auto; }
.page.pro .inv-foot { margin-top: auto; padding-top: 18px; }
/* Foot: the share QR as a card at the start side, a framed box per signature
   at the end. */
.pro-foot { display: flex; align-items: stretch; justify-content: space-between; gap: 24px;
  border-top: 1px solid #e3e6ee; padding-top: 16px; break-inside: avoid; }
.pro-qr { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
  padding: 7px 7px 5px; border: 1px solid #e3e6ee; border-radius: 8px; background: #fff; }
.pro-qr .box { width: 80px; height: 80px; background: #fff; }
.pro-qr .box svg { display: block; width: 100%; height: 100%; shape-rendering: crispEdges; }
.pro-qr .cap { font-size: calc(8.5px * var(--type, 1)); color: #545b72; font-weight: 700; }
.pro-sign { display: flex; gap: 18px; flex: 1; justify-content: flex-end; }
.pro-sign .sig { display: flex; flex-direction: column; gap: 6px; flex: 0 1 220px; min-width: 150px;
  padding: 9px 14px 12px; border: 1px solid #e3e6ee; border-radius: 8px; background: #fbfcff;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pro-sign .sig .cap { font-size: calc(9.5px * var(--type, 1)); color: #16233b; font-weight: 800; }
.pro-sign .sig .cap .alt { font-weight: 600; color: #545b72; }
.pro-sign .sig .line { display: block; flex: 1 0 auto; min-height: 44px; border-bottom: 1.5px solid #9aa3b5; }

/* Letterhead: logo · who is selling · the document's name. The seller block
   takes the middle and pushes the title to the end side. */
.page.pro .clsc-head { align-items: center; gap: 18px; }
.page.pro .clsc-logo .logo { width: 84px; max-height: 84px; }
.page.pro .clsc-logo .mk { width: 64px; height: 64px; font-size: 20px; background: #16233b; color: #fff;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pro-seller { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pro-seller .nm { font-size: calc(17px * var(--type, 1)); font-weight: 800; color: #16233b; line-height: 1.25; }
.pro-seller .tg { font-size: calc(10px * var(--type, 1)); color: #6b7489; }
.pro-seller .ct, .pro-seller .ad { font-size: calc(9.5px * var(--type, 1)); color: #545b72; line-height: 1.5; }
.pro-seller .ct { display: flex; flex-wrap: wrap; gap: 2px 12px; margin-top: 2px; }
.page.pro .clsc-title { flex: 0 0 auto; }

/* Bill-to card: a small caps label, the name first and largest. The issuer
   paragraph beside it only repeated the letterhead and the meta strip. */
.page.pro .clsc-cust { min-width: 46%; border: 1px solid #e3e6ee; border-inline-start: 3px solid #16233b; }
.page.pro .clsc-cust .pro-k { font-size: calc(8.5px * var(--type, 1)); font-weight: 700; letter-spacing: .08em;
  text-transform: uppercase; color: #8a92a6; margin-bottom: 3px; }
.page.pro .clsc-cust .pro-name { font-size: calc(13.5px * var(--type, 1)); font-weight: 800; color: #16233b; line-height: 1.35; }
.page.pro .clsc-cust .pro-line { font-weight: 500; color: #545b72; font-size: calc(10.5px * var(--type, 1)); line-height: 1.55; }
.page.pro .clsc-meta { display: none; }

/* Totals as a card: hairline frame, rounded, the navy grand total its last-but
   rows' anchor. */
.page.pro table.ctot { border: 1px solid #e3e6ee; border-radius: 8px; border-collapse: separate; border-spacing: 0;
  overflow: hidden; }
.page.pro .ctot tr + tr th, .page.pro .ctot tr + tr td { border-top: 1px solid #f0f2f7; }
.page.pro .ctot tr.cash th, .page.pro .ctot tr.cash td { background: #ecfdf5; color: #065f46; }
.page.pro .ctot tr.cash-sub th, .page.pro .ctot tr.cash-sub td { border-top: 0; color: #4b5563; }

/* The closing band: the thanks at the start, how to reach the shop at the end,
   white on the letterhead's navy — the sheet ends as deliberately as it began. */
.pro-bottom { margin-top: 14px; padding: 10px 16px; border-radius: 8px; background: #16233b; color: #fff;
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 4px 18px;
  break-inside: avoid; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pro-bottom .ty { font-size: calc(11.5px * var(--type, 1)); font-weight: 800; color: #fff; letter-spacing: .02em; }
.pro-bottom .ty .alt { font-weight: 600; color: #cfd6e6; }
.pro-bottom .ct { display: flex; flex-wrap: wrap; align-items: center; gap: 2px 0;
  font-size: calc(9.5px * var(--type, 1)); color: #d7dcea; }
.pro-bottom .ct > span + span::before { content: ''; display: inline-block; width: 4px; height: 4px; border-radius: 50%;
  background: #7d89a6; margin: 0 9px; vertical-align: middle; }
.pro-bottom .ct .nm { font-weight: 800; color: #fff; }
/* The totals card, the words and the rate note each stay whole — a long
   invoice used to start its totals at the foot of one page and finish them on
   the next. */
.page.pro table.ctot, .page.pro .words, .page.pro .fxnote { break-inside: avoid; }
/* The rate note: a quiet amber notice, not a dashed box that reads like an error. */
.page.pro .fxnote { margin-top: 8px; padding: 6px 12px; border: 1px solid #f1dcae; border-radius: 8px;
  background: #fffaf0; color: #6b5212; font-size: calc(10px * var(--type, 1));
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.page.dense:not(.plain) .pro-bottom { margin-top: 8px; padding: 6px 12px; }
.page.dense:not(.plain) .pro-sign .sig .line { min-height: 30px; }

/* ── Compact (the "dense" switch) on the A4 faces ────────────────────────────
   On A5 the switch tightens rows (rules above); on A4 it used to be a no-op —
   the base rows were already at its 3px. Here it means business: the whole
   sheet gives its slack to the item rows so one page carries far more lines.
   Everything shrinks EXCEPT the type the customer actually reads — a row is
   made of padding and picture, not of smaller words. The letterhead halves,
   the rules and gaps close up, the photos drop to thumbnail size (hide the
   column entirely for the tightest sheet), and the foot pulls in. Higher
   specificity than the base/pro rules via :not(.plain), so A5 keeps its own
   compact behaviour untouched. */
.page.dense:not(.plain) .inv-top { padding-top: 10px; }
.page.dense:not(.plain) .clsc-logo .logo { width: 60px; max-height: 60px; }
.page.dense:not(.plain) .clsc-logo .mk { width: 44px; height: 44px; font-size: 15px; }
.page.dense:not(.plain) .clsc-title .dt { font-size: calc(18px * var(--type, 1)); margin-bottom: 2px; }
.page.dense:not(.plain) .inv-top hr.chr1 { margin-top: 8px; }
.page.dense:not(.plain) hr.chr { margin: 6px 0; }
.page.dense:not(.plain) .clsc-info { margin: 8px 0; }
.page.dense:not(.plain) .clsc-meta p { margin-bottom: 0; }
.page.dense:not(.plain) .pro-meta { margin-top: 8px; }
.page.dense:not(.plain) .pro-meta .cell { padding: 4px 10px; }
.page.dense:not(.plain) .clsc-cust { padding: 6px 10px; }
.page.dense:not(.plain) table.items { margin-bottom: 8px; }
.page.dense:not(.plain) .items th, .page.dense:not(.plain) .items td { padding: 1px 2px; }
.page.dense:not(.plain) .items .thumb { width: 30px; }
.page.dense:not(.plain) .items .thumb img { width: 30px; padding: 1px; border-radius: 4px; }
.page.dense:not(.plain) .ctot th, .page.dense:not(.plain) .ctot td { padding: 1px 10px; }
.page.dense:not(.plain) .words { margin-top: 8px; padding: 4px 10px; }
.page.dense:not(.plain) .words .wline { line-height: 1.5; }
.page.dense:not(.plain) .clsc-notes { margin: 6px 0 0; padding: 4px 8px; }
.page.dense:not(.plain) .inv-foot { padding-bottom: 10px; }
.page.dense:not(.plain) .clsc-dept { font-size: 12px; }
.page.dense:not(.plain) .pro-foot { padding-top: 8px; margin-top: 6px; }
.page.dense:not(.plain) .pro-qr .box { width: 64px; height: 64px; }
.page.dense:not(.plain) .pro-sign .sig .line { height: 18px; }

/* Level 2 — EXTRA compact. The one level allowed to touch the line type: a
   step down to 9px is the last real row-height left to claim once compact has
   taken the padding and the pictures. Everything else just tightens further. */
.page.dense2:not(.plain) { line-height: 1.35; }
.page.dense2:not(.plain) .inv-top { padding-top: 6px; }
.page.dense2:not(.plain) .clsc-logo .logo { width: 44px; max-height: 44px; }
.page.dense2:not(.plain) .clsc-title .dt { font-size: calc(15px * var(--type, 1)); margin-bottom: 1px; }
.page.dense2:not(.plain) .inv-top hr.chr1 { margin-top: 5px; }
.page.dense2:not(.plain) hr.chr { margin: 4px 0; }
.page.dense2:not(.plain) .clsc-info { margin: 5px 0; }
.page.dense2:not(.plain) table.items { margin-bottom: 5px; }
.page.dense2:not(.plain) .items th, .page.dense2:not(.plain) .items td { padding: 0 2px; font-size: calc(9px * var(--type, 1)); }
.page.dense2:not(.plain) .items .thumb { width: 22px; }
.page.dense2:not(.plain) .items .thumb img { width: 22px; }
.page.dense2:not(.plain) .items tbody td .subdesc { font-size: calc(8px * var(--type, 1)); }
.page.dense2:not(.plain) .ctot th, .page.dense2:not(.plain) .ctot td { padding: 0 8px; font-size: calc(10px * var(--type, 1)); }
.page.dense2:not(.plain) .words { margin-top: 5px; padding: 3px 8px; }
.page.dense2:not(.plain) .words .wline { font-size: calc(9.5px * var(--type, 1)); }
.page.dense2:not(.plain) .clsc-notes { margin-top: 4px; padding: 3px 8px; }
.page.dense2:not(.plain) .inv-foot { padding-bottom: 6px; }
.page.dense2:not(.plain) .pro-meta .cell { padding: 3px 8px; }

@media print {
  /* White behind the sheet too: printed with background graphics on, a short
     last page showed the app's dark theme under the paper's end. */
  html, body { background: #fff !important; }
  .docview { background: #fff; }
  .dv-bar { display: none !important; }
  .dv-stage { padding: 0; overflow: visible; display: block; }
  .page { box-shadow: none; border-radius: 0; width: 100%; min-height: 0; }
  /* The @page margin is the whitespace now; keeping the screen padding too
     would double it on page 1 only. */
  .page:not(.plain) .inv-top { padding: 0; }
  .page:not(.plain) .inv-body { padding: 0; }
  .page:not(.plain) .inv-foot { padding: 0 0 12px; }
  /* The blank space is the @page margin now, so padding here would double it,
     and min-height:100vh would push a blank trailing sheet off a ~150mm content
     box. .page.plain is 0,2,0 so it beats .page whatever the rule order. */
  .page.plain { padding: 0; width: 100%; min-height: 0; }
  .plain-reserve { display: none; }
  .roll { box-shadow: none; width: 100%; }
  /* Paper is A4 whatever the screen was: a transform here would break the
     browser's pagination, and the printer has no viewport to fit to. */
  .paper-fit { width: auto; max-width: none; height: auto; }
  .paper-fit > .page { max-width: 100%; transform: none; }
}

/* ---- the rail lives LAST on purpose ----
   Everything in it overrides a base rule of the same specificity, and the
   cascade settles ties by source order. Sitting above those rules it lost
   every one of them: the display:contents on .dv-foot un-wrapped the footer so
   Print rendered near the top, and the flex-wrap on .dv-opts put the options
   back into columns and took the scrolling with it. Anything added here must
   stay BELOW the rules it overrides — and no backticks in these comments, the
   whole stylesheet is a template literal and one closes it. */
/* ---- the control rail ----------------------------------------------------
   Wide screens only. The sheet is a fixed-size page the stage scales to fit, so
   every pixel the toolbar took across the top was one the paper could not have.

   Laid out as one system rather than as a toolbar turned sideways: a fixed
   identity strip at the top, a scrolling body of sections in the middle, and
   the action pinned to the foot. Everything inside shares one row height, one
   label size and one pair of edges, because a rail that lines up is most of
   what "finished" looks like at this size.

   The rail is physically on the LEFT in both directions, which is why the order
   is set per dir: in RTL the last flex child is the leftmost, in LTR the first. */
@media (min-width: 1100px) {
  .docview { flex-direction: row; align-items: stretch; }
  .dv-bar { order: 2; flex: 0 0 320px; flex-direction: column; align-items: stretch;
    height: 100vh; overflow: hidden; flex-wrap: nowrap; gap: 0; padding: 0;
    border-bottom: 0; border-inline-start: 1px solid var(--color-border); }
  .docview[dir="ltr"] .dv-bar { order: 0; border-inline-start: 0; border-inline-end: 1px solid var(--color-border); }
  .dv-bar .dv-spacer { display: none; }

  /* 1 — identity. Fixed, so the document you are printing is never scrolled
     away from the controls that are changing it. */
  .dv-bar .dv-back { order: 0; width: auto; align-self: flex-start; height: 26px;
    margin: 10px 12px 0; border: 0; background: transparent; padding: 0 2px; font-size: 12px; }
  .dv-bar .dv-title { order: 1; padding: 2px 12px 10px; font-size: 14px; line-height: 1.35;
    border-bottom: 1px solid var(--color-border); }

  /* 2 — the body. One scroll for every choice; the sections below rule
     themselves off rather than floating in whitespace. */
  .dv-bar .dv-seg { order: 2; }
  .dv-bar .dv-mm { order: 3; }
  .dv-bar .dv-check { order: 4; }
  .dv-bar .dv-opt-btn { order: 5; }
  .dv-opts { order: 6; }
  .dv-bar .dv-seg, .dv-bar .dv-mm, .dv-bar .dv-check, .dv-bar .dv-opt-btn, .dv-opts {
    width: auto; margin: 0 12px; }
  .dv-bar .dv-seg:first-of-type { margin-top: 10px; }
  .dv-bar .dv-seg { display: flex; gap: 2px; padding: 3px; }
  .dv-bar .dv-seg button { flex: 1 1 0; min-width: 0; height: 28px; padding: 0 6px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dv-bar .dv-check { height: 30px; margin-top: 8px; }
  .dv-bar .dv-mm { height: auto; flex-wrap: wrap; padding: 8px 10px; margin-top: 8px; }
  .dv-bar .dv-opt-btn { height: 30px; margin-top: 8px; justify-content: center; }
  /* One column, and it is the part that scrolls. flex-wrap has to be beaten
     explicitly — inherited from the bar's row it was folding the groups into
     two columns, which in a 320px rail is two half-columns of nothing. */
  .dv-opts { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
    flex-direction: column; flex-wrap: nowrap; gap: 0; margin-top: 10px; padding-bottom: 12px; }
  /* Each group a plain stack: no box, no rule down the side — one row per
     option, top to bottom. */
  .dv-opts .grp { width: 100%; flex: 0 0 auto; flex-direction: column; align-items: flex-start;
    flex-wrap: nowrap; padding: 10px 0; gap: 6px; border: 0; }
  .dv-opts .grp.note { flex: 0 0 auto; }
  .dv-opts .grp + .grp { border-inline-start: 0; padding-inline-start: 0;
    border-top: 1px solid var(--color-border); }
  /* The swatches are the one thing that should sit side by side — five colours
     in a column would be a list of nothing. */
  .dv-opts .grp .dv-swatch { flex: 0 0 auto; }
  /* …so the colour group keeps its own row of them. */
  .dv-opts .grp:has(.dv-swatch) { flex-direction: row; flex-wrap: wrap; align-items: center; }
  .dv-opts .grp:has(.dv-swatch) .lbl { width: 100%; }
  .dv-opts .grp.note textarea, .dv-opts .grp.note .hint, .dv-opts .grp.note .lbl { width: 100%; }
  /* A row reads left to right and stops: box, name, and its width where the
     name ends. Pushing the control to the far edge lined them up in a second
     column and turned a list of choices into a table with a gutter down it —
     which is not what a panel of switches is. */
  .dv-opts .dv-check { width: auto; min-height: 24px; gap: 6px; margin: 0; }
  .dv-opts .grp.note { padding-bottom: 4px; }

  /* 3 — the action. Pinned, because it is the end of every path through the
     rail and should not have to be scrolled back to. */
  .dv-foot { order: 7; margin-top: auto; display: flex; flex-direction: column; gap: 8px;
    padding: 10px 12px; border-top: 1px solid var(--color-border); background: var(--color-bg); }
  .dv-foot .dv-wa-share, .dv-foot .dv-print, .dv-foot .dv-wa, .dv-foot .dv-app { width: 100%; justify-content: center; }
  /* The STAFF send (.dv-wa) and the customer's share (.dv-wa-share) are two
     different elements and only ever one of them renders. Both are actions, so
     both live in the foot beside Print — the staff one was outside it, which
     with no order of its own put it up beside Back. */
  .dv-foot .dv-wa { height: 34px; }
  /* One saturated button in the rail. Green next to brand-blue at the same
     weight reads as two primary actions, and only one of them is. */
  .dv-foot .dv-wa-share { height: 34px; background: transparent; color: var(--color-fg);
    border: 1px solid var(--color-border); }
  .dv-foot .dv-print { height: 40px; }

  .dv-stage { flex: 1; min-width: 0; height: 100vh; }
}

`;
